import { safeSetDoc, safeUpdateDoc, safeAddDoc, safeDeleteDoc, compressImage, safeWriteBatch, reconcileSectionOrder, authedFetch } from './utils';
import { useReducer, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as exifr from 'exifr';
import {
  collection,
  query,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  orderBy,
  limit,
  where,
  getDocs,
} from 'firebase/firestore';
import { signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, db } from './firebase';
import { appId as APP_ID_CONSTANT, version, CLIENT_VERSION, MUTED_COLORS } from './constants';
import DesktopLayout from './components/DesktopLayout';
import MobileHome from './components/MobileHome';
import MobileProjectView from './components/MobileProjectView';
import MobileChatList from './components/MobileChatList';
import MobileSettings from './components/MobileSettings';
import LoginScreen from './components/LoginScreen';
import UserProfileModal from './components/UserProfileModal';
import Avatar from './components/Avatar';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProjectDataProvider, useProject } from './contexts/ProjectDataContext';
import { useNavigateWithParams } from './hooks/useNavigateWithParams';
import { useIdleGate } from './hooks/useIdleGate';
import IdleRefreshOverlay from './components/IdleRefreshOverlay';
import CitySearch from './components/CitySearch';
import { SmartTimeInput } from './components/SmartTimeInput';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCenter,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { ArrowLeft, Calendar as CalendarIcon, Plus, X, Check, Star, Sparkles } from 'lucide-react';
import { useFlightTracker } from './hooks/useFlightTracker';
import { useNavigate, useLocation, Routes, Route, useParams } from 'react-router-dom';
import TasksView from './components/TasksView';
import ChatView from './components/ChatView';
import NotepadView from './components/NotepadView';
import MobileCalendarView from './components/MobileCalendarView';
import { DraggableModal } from './components/DraggableModal';
import LateNightHabitModal from './components/LateNightHabitModal';
import DatabaseView from './components/DatabaseView';
import TagManagerPage from './components/TagManagerPage';
import SocialFeedView from './components/SocialFeedView';
import EventChangesView from './components/EventChangesView';
import Breadcrumbs from './components/Breadcrumbs';
import {
  linkify,
  normalizeDate,
  getUSHolidays,
  getItemColor,
  getTimezoneOffsetHours,
} from './utils';

import Clock from './components/Clock';

const SoloWrapper = ({ children, title, path, onNavigate, fallbackName, headerClocks, hideHeader }) => {
  const navigate = useNavigate();
  return (
    <div
      data-testid="solo-wrapper"
      className="h-screen w-screen bg-white flex flex-col overflow-hidden print:h-auto print:overflow-visible"
    >
      {!hideHeader && (
        <div className="h-14 border-b border-zinc-100 flex items-center justify-between px-6 flex-shrink-0 bg-white/80 backdrop-blur-md z-10 print:hidden">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-400 hover:text-zinc-900 flex-shrink-0"
            >
              <ArrowLeft size={20} />
            </button>
            {path ? (
              <Breadcrumbs
                path={path}
                onNavigate={onNavigate}
                currentModule={title}
                fallbackName={fallbackName}
              />
            ) : (
              <h1 className="text-sm font-black text-zinc-900 uppercase tracking-widest">{title}</h1>
            )}
          </div>
          <div className="flex items-center gap-1 bg-zinc-100/50 p-1 rounded-xl border border-zinc-200/50">
            {(headerClocks || []).map((clock, idx) => (
              <Clock
                key={idx}
                timezone={clock.timezone}
                city={clock.city}
                lat={clock.lat}
                lng={clock.lng}
              />
            ))}
          </div>
        </div>
      )}
      <main className="flex-1 min-h-0 relative print:h-auto print:overflow-visible">{children}</main>
    </div>
  );
};

const getDmUser = (channel, currentUser, allUsers) => {
  if (!channel || channel.type !== 'dm' || !currentUser) return null;
  const otherId = (channel.participants || []).find((id) => id !== currentUser.uid);
  return (allUsers || []).find((u) => u.id === otherId);
};

const getProjectPath = (channelId, channels) => {
  if (!channelId || channelId === 'general_chat_global' || channelId.startsWith('personal-'))
    return [];
  const path = [];
  let current = (channels || []).find((c) => c.id === channelId);
  while (current) {
    path.unshift(current);
    current = (channels || []).find((c) => c.id === current.parentId);
  }
  return path;
};

const getRootProject = (channelId, channels) => {
  const path = getProjectPath(channelId, channels);
  return path.length > 0 ? path[0] : null;
};

const safeJsonParse = (key, fallback) => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return fallback;
    return JSON.parse(item);
  } catch (e) {
    return fallback;
  }
};

const getTodayStr = () => {
  const tzOffset = (new Date()).getTimezoneOffset() * 60000;
  return new Date(Date.now() - tzOffset).toISOString().slice(0, 10);
};

const initialState = {
  isTableActive: false,
  isTableMenuOpen: false,
  isSpellCheckEnabled: true,
  messages: [],
  messageLimit: 50,
  isCreatingChannel: false,
  newChannelName: '',
  newChannelColor: 'default',
  newChannelParentId: null,
  createError: '',
  editingChannelId: null,
  editChannelName: '',
  editChannelDescription: '',
  editChannelColor: 'default',
  editChannelParticipants: [],
  editError: '',
  newTask: '',
  newTaskPriority: 'High',
  newTaskDate: '',
  newTaskDueDate: '',
  newTaskIsHeader: false,
  editingTaskId: null,
  editTaskText: '',
  editTaskPriority: 'High',
  editTaskDueDate: '',
  editTaskVenueName: '',
  editTaskAddress: '',
  editTaskAddressLat: null,
  editTaskAddressLng: null,
  deletingTaskId: null,
  editingMessageId: null,
  confirmDeleteMessageId: null,
  activeId: null,
  activeType: null,
  activeTitle: null,
  expandedIds: {},
  activeButtonsId: null,
  mobileSectionOrder: reconcileSectionOrder(safeJsonParse('mobileSectionOrder', null)),
  collapsedHeaderIds: safeJsonParse('collapsedHeaderIds', {}),
  hiddenCompletedHeaderIds: safeJsonParse('hiddenCompletedHeaderIds', {}),
  showCompletedTasks: localStorage.getItem('showCompletedTasks') !== 'false',
  showOnlyStarredTasks: localStorage.getItem('showOnlyStarredTasks') === 'true',
  taskFontSize: parseInt(localStorage.getItem('taskFontSize') || '13'),
  calendarFontSize: parseInt(localStorage.getItem('calendarFontSize') || '9'),
  calendarCellHeight: localStorage.getItem('calendarCellHeight') || 'small',
  calendarDate: {},
  radioStation: null,
  isPlayingRadio: false,
  visibleCalendars: safeJsonParse('visibleCalendars', [
    'nytm-event',
    'nytm-tasks-h',
    'nytm-tasks-m',
    'nytm-tasks-l',
    'nytm-flight',
    'nytm-hotel',
    'nytm-birthday',
    'nytm-holiday',
    'nytm-tentative',
    'nytm-habit',
  ]),
  chatInputHtml: '',
  isSavingNote: false,
  activeChannelDescription: '',
  googleCalendars: [],
  googleEvents: [],
  enabledCalendars: [
    'nytm-tasks-h',
    'nytm-tasks-m',
    'nytm-tasks-l',
    'nytm-flight',
    'nytm-hotel',
    'nytm-birthday',
    'nytm-event',
    'nytm-tentative',
    'nytm-concert',
    'nytm-venue',
    'nytm-restaurant',
    'nytm-sight',
    'nytm-shopping',
    'nytm-holiday',
    'nytm-habit',
  ],
  isAddingEvent: false,
  editingEventId: null,
  editingEventChannelId: null,
  newEventTitle: '',
  newEventStartDate: '',
  newEventEndDate: '',
  newEventType: 'event',
  newEventIsStarred: false,
  newEventPersonName: '',
  newEventBirthYear: '',
  newEventIsAllDay: true,
  newEventStartTime: '',
  newEventEndTime: '',
  newTripCity: '',
  newTripState: '',
  newTripCountry: '',
  newTripTimezone: '',
  newTripLat: null,
  newTripLng: null,
  newTripFlightIn: [],
  newTripFlightOut: [],
  newTripHotels: [],
  newEventVenueName: '',
  newEventAddress: '',
  newEventAddressLat: null,
  newEventAddressLng: null,
  newEventNote: '',
  newEventHasLocation: false,
  newEventCity: '',
  newEventLat: null,
  newEventLng: null,
  newEventTimezone: '',
  newEventTimezoneSource: 'home', // 'home' or 'trip' or 'override' or 'place'
  newEventPlaceCity: '',
  newEventPlaceTimezone: '',
  newEventOverrideCity: '',
  newEventOverrideTimezone: '',
  newEventBio: '',
  newEventMotivation: '',
  newEventCalendarName: '',
  isDayDetailOpen: false,
  selectedDayId: null,
  appId: APP_ID_CONSTANT,
  appVersion: version,
  isImporting: false,
  importProgress: 0,
  pendingImportData: null,
  selectedProfileUser: null,
  isOfflineUsersOpen: false,
  activeUploads: {}, // TRACKING BACKGROUND UPLOADS
  activeChannelId: 'general_chat_global',
  chatChannelId: 'general_chat_global',
  openDMChannelIds: [],
  dmMessagesMap: {},
  currentView: 'home',
  pendingDragEvent: null,
  isLocationConflictOpen: false,
  advancedFilters: sessionStorage.getItem('nytm-advanced-filters') ? JSON.parse(sessionStorage.getItem('nytm-advanced-filters')) : null,
  isFilterPanelOpen: false,
  isMapPanelOpen: false,
  isDatabasePanelOpen: false,
  mapStartDate: localStorage.getItem('nytm-map-start-date') || getTodayStr(),
  mapEndDate: localStorage.getItem('nytm-map-end-date') || '',
  searchRadius: localStorage.getItem('nytm-search-radius') === '' ? '' : parseInt(localStorage.getItem('nytm-search-radius') || '50', 10),
  customCenter: safeJsonParse('nytm-custom-center', null),
  friendFilter: localStorage.getItem('nytm-friend-filter') || 'all',
  selectedTripId: localStorage.getItem('nytm-selected-trip-id') || 'all',
  dashboardScale: parseFloat(localStorage.getItem('nytm-dashboard-scale') || '1.0'),
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_DASHBOARD_SCALE':
      localStorage.setItem('nytm-dashboard-scale', String(action.payload));
      return { ...state, dashboardScale: action.payload };
    case 'SET_ADVANCED_FILTERS':
      sessionStorage.setItem('nytm-advanced-filters', JSON.stringify(action.payload));
      return { ...state, advancedFilters: action.payload };
    case 'TOGGLE_FILTER_PANEL':
      return { ...state, isFilterPanelOpen: !state.isFilterPanelOpen };
    case 'TOGGLE_MAP_PANEL':
      return { ...state, isMapPanelOpen: !state.isMapPanelOpen, isDatabasePanelOpen: false };
    case 'TOGGLE_DATABASE_PANEL':
      return { ...state, isDatabasePanelOpen: !state.isDatabasePanelOpen, isMapPanelOpen: false };
    case 'SET_MAP_FILTER_STATE': {
      const next = { ...state, ...action.payload };
      if ('mapStartDate' in action.payload) localStorage.setItem('nytm-map-start-date', action.payload.mapStartDate || '');
      if ('mapEndDate' in action.payload) localStorage.setItem('nytm-map-end-date', action.payload.mapEndDate || '');
      if ('searchRadius' in action.payload) localStorage.setItem('nytm-search-radius', action.payload.searchRadius === '' ? '' : String(action.payload.searchRadius));
      if ('customCenter' in action.payload) localStorage.setItem('nytm-custom-center', JSON.stringify(action.payload.customCenter));
      if ('friendFilter' in action.payload) localStorage.setItem('nytm-friend-filter', action.payload.friendFilter);
      if ('selectedTripId' in action.payload) localStorage.setItem('nytm-selected-trip-id', action.payload.selectedTripId);
      return next;
    }
    case 'TOGGLE_MEDIA_PLAYER': {
      const nextOpen = action.payload !== undefined ? action.payload : !state.isMediaPlayerOpen;
      localStorage.setItem('isMediaPlayerOpen', nextOpen);
      return { ...state, isMediaPlayerOpen: nextOpen };
    }
    case 'TOGGLE_MEDIA_PLAYER_COMPACT': {
      const nextCompact = action.payload !== undefined ? action.payload : !state.isMediaPlayerCompact;
      localStorage.setItem('isMediaPlayerCompact', nextCompact);
      return { ...state, isMediaPlayerCompact: nextCompact };
    }
    case 'SET_SELECTED_PROFILE':
      return { ...state, selectedProfileUser: action.payload };
    case 'SET_OPEN_DM': {
      const channelId = action.payload;
      // If already open, move to front (left)
      const otherIds = (state.openDMChannelIds || []).filter((id) => id !== channelId);
      const nextIds = [channelId, ...otherIds].slice(0, 3);
      return { ...state, openDMChannelIds: nextIds };
    }
    case 'CLOSE_DM': {
      const channelId = action.payload;
      const nextIds = (state.openDMChannelIds || []).filter((id) => id !== channelId);
      const nextMap = { ...state.dmMessagesMap };
      delete nextMap[channelId];
      return { ...state, openDMChannelIds: nextIds, dmMessagesMap: nextMap };
    }
    case 'SET_DM_MESSAGES': {
      const { channelId, messages } = action.payload;
      return {
        ...state,
        dmMessagesMap: {
          ...state.dmMessagesMap,
          [channelId]: messages,
        },
      };
    }
    case 'SET_IMPORTING':
      return { ...state, isImporting: action.payload };
    case 'SET_IMPORT_PROGRESS':
      return { ...state, importProgress: action.payload };
    case 'TRIGGER_IMPORT':
      return { ...state, pendingImportData: action.payload };
    case 'SET_RADIO_STATION':
      return { ...state, radioStation: action.payload };
    case 'SET_IS_PLAYING_RADIO':
      return { ...state, isPlayingRadio: action.payload };
    case 'LOGOUT':
      return {
        ...initialState,
        activeChannelId: 'general_chat_global',
        chatChannelId: 'general_chat_global',
      };
    case 'SET_SELECTED_HEADER':
      return {
        ...state,
        selectedHeaderId: state.selectedHeaderId === action.payload ? null : action.payload,
      };
    case 'TOGGLE_COLLAPSE_HEADER': {
      const nextCollapsed = {
        ...state.collapsedHeaderIds,
        [action.payload]: !state.collapsedHeaderIds[action.payload],
      };
      localStorage.setItem('collapsedHeaderIds', JSON.stringify(nextCollapsed));
      return { ...state, collapsedHeaderIds: nextCollapsed };
    }
    case 'TOGGLE_HIDE_COMPLETED_HEADER': {
      const nextHidden = {
        ...state.hiddenCompletedHeaderIds,
        [action.payload]: !state.hiddenCompletedHeaderIds[action.payload],
      };
      localStorage.setItem('hiddenCompletedHeaderIds', JSON.stringify(nextHidden));
      return { ...state, hiddenCompletedHeaderIds: nextHidden };
    }
    case 'COLLAPSE_ALL_HEADERS': {
      const { tasks, personalTasks, currentView } = action.payload;
      const allListTasks = currentView === 'home' ? personalTasks : tasks;
      const headers = (allListTasks || []).filter(
        (t) => t.isHeader || (t.text || '').trim().endsWith(':')
      );
      const anyExpanded = headers.some((h) => !state.collapsedHeaderIds[h.id]);
      const nextCollapsed = { ...state.collapsedHeaderIds };
      headers.forEach((h) => {
        nextCollapsed[h.id] = anyExpanded;
      });
      localStorage.setItem('collapsedHeaderIds', JSON.stringify(nextCollapsed));
      return { ...state, collapsedHeaderIds: nextCollapsed };
    }
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'SET_ACTIVE_CHANNEL': {
      const channelId = action.payload;
      return {
        ...state,
        activeChannelId: channelId,
        chatChannelId: channelId,
        currentView: 'workspace',
        selectedHeaderId: null,
      };
    }
    case 'SET_CHAT_CHANNEL':
      return { ...state, chatChannelId: action.payload };
    case 'GO_HOME':
      return {
        ...state,
        activeChannelId: 'general_chat_global',
        chatChannelId: 'general_chat_global',
        currentView: 'home',
        selectedHeaderId: null,
      };
    case 'SET_VIEW':
      return {
        ...state,
        currentView: action.payload,
        selectedHeaderId: action.payload === 'home' ? null : state.selectedHeaderId,
      };
    case 'DRAG_START':
      return { 
        ...state, 
        activeId: action.payload.id, 
        activeType: action.payload.type,
        activeTitle: action.payload.title 
      };
    case 'DRAG_END':
      return { ...state, activeId: null, activeType: null, activeTitle: null };
    case 'TOGGLE_EXPANDED': {
      const { id, pathIds = [] } =
        typeof action.payload === 'string' ? { id: action.payload } : action.payload;
      const newExpanded = {};
      const wasExpanded = !!state.expandedIds[id];

      if (wasExpanded) {
        Object.keys(state.expandedIds).forEach((key) => {
          if (key !== id) newExpanded[key] = true;
        });
      } else {
        pathIds.forEach((pid) => {
          newExpanded[pid] = true;
        });
        newExpanded[id] = true;
      }
      return { ...state, expandedIds: newExpanded };
    }
    case 'EXPAND_PATH': {
      const expandedWithPath = { ...state.expandedIds };
      let changed = false;
      (action.payload || []).forEach((id) => {
        if (!expandedWithPath[id]) {
          expandedWithPath[id] = true;
          changed = true;
        }
      });
      return changed ? { ...state, expandedIds: expandedWithPath } : state;
    }
    case 'SET_CHAT_INPUT':
      return { ...state, chatInputHtml: action.payload };
    case 'SET_REACTION_FILTER':
      return { ...state, reactionFilter: action.payload };
    case 'SET_ACTIVE_BUTTONS': {
      const { id, pathIds } = action.payload;
      let newExpanded = { ...state.expandedIds };
      
      if (id && newExpanded[id]) {
        delete newExpanded[id];
        return {
          ...state,
          activeButtonsId: state.activeButtonsId === id ? null : state.activeButtonsId,
          expandedIds: newExpanded,
        };
      }

      const isRoot = !pathIds || pathIds.length <= 1;
      if (isRoot) {
        newExpanded = {};
      }

      (pathIds || []).forEach((pid) => {
        newExpanded[pid] = true;
      });
      if (id) newExpanded[id] = true;
      return { ...state, activeButtonsId: id, expandedIds: newExpanded };
    }
    case 'SET_NEW_TASK':
      return { ...state, newTask: action.payload };
    case 'SET_NEW_TASK_PRIORITY':
      return { ...state, newTaskPriority: action.payload };
    case 'SET_NEW_TASK_DATE':
      return { ...state, newTaskDate: action.payload };
    case 'SET_NEW_CHANNEL_INPUT':
      return { ...state, ...action.payload };
    case 'OPEN_CREATE_CHANNEL':
      return { ...state, isCreatingChannel: true, newChannelParentId: action.payload || null };
    case 'CLOSE_CREATE_CHANNEL':
      return {
        ...state,
        isCreatingChannel: false,
        newChannelName: '',
        newChannelColor: 'default',
        newChannelParentId: null,
        createError: '',
      };
    case 'OPEN_EDIT_CHANNEL':
      return {
        ...state,
        editingChannelId: action.payload.id,
        editChannelName: action.payload.name,
        editChannelDescription: action.payload.description || '',
        editChannelColor: action.payload.color || 'default',
        editChannelParticipants: action.payload.participants || [],
        editError: '',
      };
    case 'CLOSE_EDIT_CHANNEL':
      return {
        ...state,
        editingChannelId: null,
        editChannelName: '',
        editChannelDescription: '',
        editChannelColor: 'default',
        editChannelParticipants: [],
        editError: '',
      };
    case 'OPEN_LOCATION_CONFLICT':
      return { ...state, isLocationConflictOpen: true, pendingDragEvent: action.payload };
    case 'CLOSE_LOCATION_CONFLICT':
      return { ...state, isLocationConflictOpen: false, pendingDragEvent: null };
    case 'SET_EDIT_CHANNEL_INPUT':
      return { ...state, ...action.payload };
    case 'SET_GOOGLE_EVENTS':
      return { ...state, googleEvents: action.payload };
    case 'SET_GOOGLE_CALENDARS':
      return { ...state, googleCalendars: action.payload };
    case 'SET_TASK_FONT_SIZE':
      localStorage.setItem('taskFontSize', action.payload.toString());
      return { ...state, taskFontSize: action.payload };
    case 'SET_CALENDAR_FONT_SIZE':
      localStorage.setItem('calendarFontSize', action.payload.toString());
      return { ...state, calendarFontSize: action.payload };
    case 'SET_CALENDAR_CELL_HEIGHT':
      localStorage.setItem('calendarCellHeight', action.payload.toString());
      return { ...state, calendarCellHeight: action.payload };
    case 'UPDATE_UPLOAD_PROGRESS': {
      const { channelId, progress } = action.payload;
      return {
        ...state,
        activeUploads: {
          ...state.activeUploads,
          [channelId]: progress,
        },
      };
    }
    case 'FINISH_UPLOAD': {
      const channelId = action.payload;
      const nextUploads = { ...state.activeUploads };
      delete nextUploads[channelId];
      return { ...state, activeUploads: nextUploads };
    }
    case 'TOGGLE_SHOW_COMPLETED': {
      const showComp = !state.showCompletedTasks;
      localStorage.setItem('showCompletedTasks', showComp.toString());
      return { ...state, showCompletedTasks: showComp };
    }
    case 'TOGGLE_SHOW_ONLY_STARRED': {
      const showStar = !state.showOnlyStarredTasks;
      localStorage.setItem('showOnlyStarredTasks', showStar.toString());
      return { ...state, showOnlyStarredTasks: showStar };
    }
    case 'SET_CALENDAR_DATE':
      return { ...state, calendarDate: { ...state.calendarDate, ...action.payload } };
    case 'TOGGLE_NEW_TASK_HEADER':
      return { ...state, newTaskIsHeader: !state.newTaskIsHeader };
    case 'SET_NEW_TASK_HEADER':
      return { ...state, newTaskIsHeader: action.payload };
    case 'SET_EDIT_TASK': {
      if (!action.payload)
        return {
          ...state,
          editingTaskId: null,
          editTaskText: '',
          editTaskPriority: 'High',
          editTaskDueDate: '',
          editTaskVenueName: '',
          editTaskAddress: '',
          editTaskAddressLat: null,
          editTaskAddressLng: null,
        };
      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      return {
        ...state,
        editingTaskId: action.payload.id,
        editTaskText: action.payload.text,
        editTaskPriority: action.payload.priority || 'High',
        editTaskDueDate: action.payload.dueDate ? action.payload.dueDate.split('T')[0] : todayStr,
        editTaskVenueName: action.payload.venueName || '',
        editTaskAddress: action.payload.address || '',
        editTaskAddressLat: action.payload.addressLat || null,
        editTaskAddressLng: action.payload.addressLng || null,
      };
    }
    case 'SET_EDIT_TASK_TEXT':
      return { ...state, editTaskText: action.payload };
    case 'SET_EDIT_TASK_PRIORITY':
      return { ...state, editTaskPriority: action.payload };
    case 'SET_EDIT_TASK_DUE_DATE':
      return { ...state, editTaskDueDate: action.payload };
    case 'SET_EDIT_TASK_INPUT':
      return { ...state, ...action.payload };
    case 'TOGGLE_OFFLINE_USERS':
      return { ...state, isOfflineUsersOpen: !state.isOfflineUsersOpen };
    case 'LOAD_MORE_MESSAGES':
      return { ...state, messageLimit: state.messageLimit + 50 };
    case 'SET_CALENDAR_VISIBILITY': {
      localStorage.setItem('visibleCalendars', JSON.stringify(action.payload));
      return { ...state, visibleCalendars: action.payload };
    }
    case 'OPEN_ADD_EVENT': {
      const isTripPayload = action.payload?.type === 'trip';
      const isHabitPayload = action.payload?.type === 'habit';
      const now = new Date();
      const todayId = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      const startDateVal = normalizeDate(
        isTripPayload || isHabitPayload
          ? action.payload.date
          : typeof action.payload === 'string'
            ? action.payload
            : action.payload?.date || todayId
      );

      let effectiveStart = startDateVal;
      if (isHabitPayload && startDateVal < todayId) {
        effectiveStart = todayId;
      }

      let defaultStart = '12:00';
      let defaultEnd = '13:00';

      if (effectiveStart === todayId) {
        const nextHour = (now.getHours() + 1) % 24;
        defaultStart = `${String(nextHour).padStart(2, '0')}:00`;
        defaultEnd = `${String((nextHour + 1) % 24).padStart(2, '0')}:00`;
      }

      return {
        ...state,
        isAddingEvent: true,
        editingEventId: null,
        newEventTitle: action.payload?.title || '',
        newEventStartDate: effectiveStart,
        newEventEndDate: isHabitPayload ? '' : effectiveStart,
        newEventType: isTripPayload ? 'trip' : isHabitPayload ? 'habit' : (action.payload?.type || 'event'),
        newEventIsStarred: false,
        newEventPriority: 'High',
        newEventPersonName: '',
        newEventBirthYear: '',
        newEventIsAllDay: !isTripPayload && !isHabitPayload, // Trips/Habits are all-day by default
        newEventStartTime: (isTripPayload || isHabitPayload) ? '' : defaultStart,
        newEventEndTime: (isTripPayload || isHabitPayload) ? '' : defaultEnd,
        newTripCity: '',
        newTripState: '',
        newTripCountry: '',
        newTripTimezone: '',
        newTripLat: null,
        newTripLng: null,
        newTripFlightIn: [''],
        newTripFlightOut: [''],
        newTripHotels: [],
        newEventVenueName: '',
        newEventAddress: '',
        newEventAddressLat: null,
        newEventAddressLng: null,
        newEventNote: '',
        newEventHasLocation: false,
        newEventCity: '',
        newEventLat: null,
        newEventLng: null,
        newEventTimezone: '',
        newEventTimezoneSource: 'home',
        newEventPlaceCity: '',
        newEventPlaceTimezone: '',
        newEventOverrideCity: '',
        newEventOverrideTimezone: '',
      };
    }
    case 'OPEN_EDIT_EVENT': {
      const flightIn = action.payload.flightIn;
      const flightOut = action.payload.flightOut;
      const isTask = action.payload.type === 'task';
      
      return {
        ...state,
        isAddingEvent: true,
        editingEventId: action.payload.originalId || action.payload.id,
        editingEventChannelId: action.payload.channelId || null,
        newEventTitle: action.payload.title || action.payload.text || '',
        newEventStartDate: normalizeDate(action.payload.startDate || action.payload.taskDate || action.payload.dueDate || action.payload.date),
        newEventEndDate: normalizeDate(action.payload.endDate || action.payload.taskDate || action.payload.dueDate || action.payload.date),
        newEventType: action.payload.type || 'event',
        newEventIsStarred: !!action.payload.isStarred,
        newEventPriority: action.payload.priority || 'High',
        newEventPersonName: action.payload.personName || '',
        newEventBirthYear: action.payload.birthYear || '',
        newEventIsAllDay: action.payload.isAllDay !== false,
        newEventStartTime: action.payload.startTime || '',
        newEventEndTime: action.payload.endTime || '',
        newTripCity: action.payload.city || '',
        newTripState: action.payload.state || '',
        newTripCountry: action.payload.country || '',
        newTripTimezone: action.payload.timezone || '',
        newTripLat: action.payload.lat || null,
        newTripLng: action.payload.lng || null,
        newTripFlightIn: Array.isArray(flightIn) ? flightIn : flightIn ? [flightIn] : [''],
        newTripFlightOut: Array.isArray(flightOut) ? flightOut : flightOut ? [flightOut] : [''],
        newTripHotels: action.payload.hotels || [],
        newEventVenueName: action.payload.venueName || '',
        newEventAddress: action.payload.address || action.payload.location || '',
        newEventAddressLat: action.payload.addressLat || null,
        newEventAddressLng: action.payload.addressLng || null,
        newEventNote: action.payload.note || action.payload.description || '',
        newEventHasLocation: (action.payload.type === 'tentative' || action.payload.type === 'event') && !!action.payload.city,
        newEventCity: (action.payload.type === 'tentative' || action.payload.type === 'event' ? action.payload.city : '') || '',
        newEventLat: (action.payload.type === 'tentative' || action.payload.type === 'event' ? action.payload.lat : null) || null,
        newEventLng: (action.payload.type === 'tentative' || action.payload.type === 'event' ? action.payload.lng : null) || null,
        newEventTimezone: (action.payload.type === 'tentative' || action.payload.type === 'event' ? action.payload.timezone : '') || '',
        newEventTimezoneSource: action.payload.timezoneSource || 'home',
        newEventPlaceCity: action.payload.timezoneSource === 'place' ? action.payload.city : '',
        newEventPlaceTimezone: action.payload.timezoneSource === 'place' ? action.payload.timezone : '',
        newEventOverrideCity: action.payload.timezoneSource === 'override' ? action.payload.city : '',
        newEventOverrideTimezone: action.payload.timezoneSource === 'override' ? action.payload.timezone : '',
        newEventBio: action.payload.bio || '',
        newEventMotivation: action.payload.motivationalMessage || '',
        newEventCalendarName: action.payload.calendarName || '',
      };
    }
    case 'CLOSE_ADD_EVENT':
      return {
        ...state,
        isAddingEvent: false,
        editingEventId: null,
        editingEventChannelId: null,
        newEventTitle: '',
        newEventStartDate: '',
        newEventEndDate: '',
        newEventType: 'event',
        newEventIsStarred: false,
        newEventPriority: 'High',
        newEventPersonName: '',
        newEventBirthYear: '',
        newEventIsAllDay: true,
        newEventStartTime: '',
        newEventEndTime: '',
        newTripCity: '',
        newTripState: '',
        newTripCountry: '',
        newTripTimezone: '',
        newTripLat: null,
        newTripLng: null,
        newTripFlightIn: [],
        newTripFlightOut: [],
        newTripHotels: [],
        newEventVenueName: '',
        newEventAddress: '',
        newEventAddressLat: null,
        newEventAddressLng: null,
        newEventNote: '',
        newEventHasLocation: false,
        newEventCity: '',
        newEventLat: null,
        newEventLng: null,
        newEventTimezone: '',
        newEventTimezoneSource: 'home',
        newEventPlaceCity: '',
        newEventPlaceTimezone: '',
        newEventOverrideCity: '',
        newEventOverrideTimezone: '',
        newEventBio: '',
        newEventMotivation: '',
        newEventCalendarName: '',
      };
    case 'OPEN_DAY_DETAIL':
      return { ...state, isDayDetailOpen: true, selectedDayId: action.payload };
    case 'CLOSE_DAY_DETAIL':
      return { ...state, isDayDetailOpen: false, selectedDayId: null };
    case 'SET_NEW_EVENT_INPUT':
      return { ...state, ...action.payload };
    case 'SET_THEME_COLOR':
      return { ...state, themeColor: action.payload };
    case 'MOVE_MOBILE_SECTION': {
      const { activeId, overId } = action.payload;
      let order = reconcileSectionOrder(state.mobileSectionOrder);
      const oldIdx = order.indexOf(activeId);
      const newIdx = order.indexOf(overId);
      if (oldIdx !== -1 && newIdx !== -1) {
        // arrayMove implementation inline
        const result = [...order];
        result.splice(oldIdx, 1);
        result.splice(newIdx, 0, order[oldIdx]);
        localStorage.setItem('mobileSectionOrder', JSON.stringify(result));
        return { ...state, mobileSectionOrder: result };
      }
      return state;
    }
    default:
      return state;
  }
}


function AppContent() {
  const routerLocation = useLocation();

  // Unified location object that is reactive to Router changes
  const location = useMemo(
    () => ({
      pathname: routerLocation.pathname || '/',
      search: routerLocation.search || '',
    }),
    [routerLocation.pathname, routerLocation.search]
  );

  const authContext = useAuth();
  const projectContext = useProject();
  
  // Initialize Flight Tracking
  useFlightTracker(authContext?.user, projectContext?.events);

  // Kill Switch: Force reload if old buggy versions are running
  useEffect(() => {
    const q = doc(db, 'system', 'config');
    const unsub = onSnapshot(q, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.minVersion && data.minVersion > CLIENT_VERSION) {
          console.warn('Forcing reload due to minVersion kill switch');
          window.location.reload(true);
        }
      }
    });
    return () => unsub();
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const navigate = useNavigateWithParams();

  const params = useParams();
  const [isMobile, setIsMobile] = useState(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const mobileParam = queryParams.get('mobile');
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    const isSmallScreen = window.innerWidth < 1024;
    return mobileParam === 'true' || (isMobileUA && isSmallScreen);
  });
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  const isTouch = useMemo(() => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  const taskInputRef = useRef(null);
  const taskEditRef = useRef(null);
  const chatInputRef = useRef(null);
  const notepadRef = useRef(null);
  const noteTimeoutRef = useRef(null);
  const isLocalEditRef = useRef(false);

  const { user: authUser, profile: authProfile, isBypass } = authContext;
  const [b2Token, setB2Token] = useState(null);
  const [lateNightHabitWarning, setLateNightHabitWarning] = useState(null);

  const radioAudioRef = useRef(null);
  const backupInProgressRef = useRef(false);

  useEffect(() => {
    if (!radioAudioRef.current) return;
    
    if (state.isPlayingRadio) {
      radioAudioRef.current.play().catch(e => {
        console.error("Radio play error:", e);
        dispatch({ type: 'SET_IS_PLAYING_RADIO', payload: false });
      });
      
      if ('mediaSession' in navigator && state.radioStation) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: state.radioStation.name,
          artist: 'Internet Radio',
          album: 'Now You Tree Me'
        });
        navigator.mediaSession.setActionHandler('play', () => {
          dispatch({ type: 'SET_IS_PLAYING_RADIO', payload: true });
        });
        navigator.mediaSession.setActionHandler('pause', () => {
          dispatch({ type: 'SET_IS_PLAYING_RADIO', payload: false });
        });
      }
    } else {
      radioAudioRef.current.pause();
    }
  }, [state.radioStation, state.isPlayingRadio, dispatch]);

  useEffect(() => {
    const fetchToken = async () => {
      if (!authUser?.uid) return;
      try {
        const res = await authedFetch('https://us-central1-project-project-30249.cloudfunctions.net/getBackblazeDownloadToken', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileNamePrefix: 'photos/',
            uid: authUser.uid,
            validDurationInSeconds: 86400
          })
        });
        if (res.ok) {
          const data = await res.json();
          setB2Token(data.authorizationToken);
        }
      } catch (e) {
        console.error('Failed to fetch B2 download token', e);
      }
    };
    fetchToken();
  }, [authUser]);
  const {
    channels,
    deletedChannels,
    allTasks,
    events,
    allUsers,
    habits,
    habitLogs,
    photos,
    audioTracks,
    loading: dataLoading,
  } = projectContext;

  const queryParams = new URLSearchParams(window.location.search);
  const mobileParam = queryParams.get('mobile');
  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const [isVault] = useState(isLocalHost && (queryParams.get('access') === 'kx92j' || window.__IS_VAULT_ACCESS));
  const effectiveUser =
    authUser || (isVault && isBypass ? { uid: 'test-uid-123', email: 'test@example.com' } : null);

  useEffect(() => {
    if (isVault) window.__IS_VAULT_ACCESS = true;
  }, [isVault]);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDeferredPrompt(null);
    }
  };

  const authDispatch = authContext.dispatch;
  const saveUserProfile = useCallback(async (u) => { 
    if (import.meta.env.DEV) console.log("[DEBUG] saveUserProfile called", JSON.stringify(u));
    const targetUid = authUser?.uid || (isVault ? 'test-uid-123' : null);
    if (!targetUid) return;
    try {
      await safeSetDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'users', targetUid), u, {
        merge: true,
      });
    } catch (e) {
      console.error(e);
    }
    authDispatch({ type: 'UPDATE_PROFILE_LOCAL', payload: u });
  }, [authUser?.uid, isVault, authDispatch]);

  const toggleTask = useCallback(async (id, currentCompleted) => {
    // Strip calendar suffixes for augmented items
    const cleanId = id.replace(/-(today|due|completed|project-starred-today|project-starred-due)$/, '');
    const task = (allTasks || []).find((t) => t.id === cleanId);
    if (import.meta.env.DEV) console.log('[DEBUG] toggleTask:', { originalId: id, cleanId, taskFound: !!task });
    const isPersonal = task?.channelId?.startsWith('personal-');

    if (!currentCompleted && location.pathname === '/calendar') {
      if (!window.confirm('Mark this task as complete?')) return;
    }
    const updateData = { completed: !currentCompleted };
    if (!currentCompleted) {
      updateData.priority = null;
      updateData.completedAt = serverTimestamp();
    } else {
      updateData.completedAt = null;
      if (isPersonal) {
        let newPriority = window.prompt("Which priority should this task have? (High, Medium, Low)", "Medium");
        if (newPriority === null) return; // Cancelled
        newPriority = newPriority.trim();
        newPriority = newPriority.charAt(0).toUpperCase() + newPriority.slice(1).toLowerCase();
        if (!['High', 'Medium', 'Low'].includes(newPriority)) {
          newPriority = 'Medium';
        }
        updateData.priority = newPriority;
        
        const channelTasks = (allTasks || []).filter(
          (t) => t.channelId === task.channelId && t.priority === newPriority && !t.completed
        );
        const minOrder = channelTasks.length > 0 
          ? Math.min(...channelTasks.map(t => t.order || 0)) 
          : 0;
        updateData.order = minOrder - 1;
      }
    }
    await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', cleanId), updateData);
  }, [location.pathname, allTasks]);

  const toggleTaskStar = useCallback(async (id, isStarred) => {
    const cleanId = id.replace(/-(today|due|completed|project-starred-today|project-starred-due)$/, '');
    const task = (allTasks || []).find((t) => t.id === cleanId);
    if (task?.channelId.startsWith('personal-')) return;
    await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', cleanId), {
      isStarred: !isStarred,
    });
  }, [allTasks]);

  useEffect(() => {
    const migratePersonalTasks = async () => {
      if (dataLoading || !authUser || !allTasks || allTasks.length === 0) return;
      const uid = authUser.uid;
      const channelId = `personal-${uid}`;
      const pTasks = allTasks.filter((t) => t.channelId === channelId);

      const migrationDoneKey = `migration_v2_tasks_${uid}`;
      if (localStorage.getItem(migrationDoneKey)) return;

      // Set immediately to prevent race condition loop
      localStorage.setItem(migrationDoneKey, 'true');

      const batch = safeWriteBatch(db);
      let count = 0;

      for (const task of pTasks) {
        const isHeader = task.isHeader || (task.text || '').trim().endsWith(':');
        if (isHeader) {
          batch.delete(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', task.id));
          count++;
        } else if (!task.priority || task.parentId || task.isStarred) {
          batch.update(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', task.id), {
            priority: task.priority || 'High',
            parentId: null,
            isStarred: false,
            isHeader: false,
          });
          count++;
        }

        if (count >= 450) {
          await batch.commit();
          // Reset batch if we needed to continue, but we'll just stop for safety here or let onSnapshot trigger next run
          return;
        }
      }

      if (count > 0) {
        await batch.commit();
        if (import.meta.env.DEV) console.log(`Migrated ${count} personal tasks`);
      }
    };
    migratePersonalTasks();
  }, [dataLoading, authUser, allTasks]);
  const deleteTask = useCallback(async (id) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      if (state.selectedHeaderId === id) dispatch({ type: 'SET_SELECTED_HEADER', payload: id }); // Toggles to null
      await safeDeleteDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', id));
    }
  }, [state.selectedHeaderId, dispatch]);

  const handleSaveEditTask = useCallback(async (id, text, dueDate, isHeader, priority) => {
    if (!text.trim()) return;
    const task = (allTasks || []).find((t) => t.id === id);
    const isPersonal = task?.channelId?.startsWith('personal-');
    const updateData = {
      text,
      taskDate: dueDate || null,
      dueDate: dueDate || null,
      isHeader: !!isHeader,
      priority: isPersonal ? (priority || 'High') : (task?.priority || 'High'),
      venueName: null,
      address: null,
      addressLat: null,
      addressLng: null,
      isStarred: isPersonal ? false : task?.isStarred || false,
    };
    if (isPersonal && priority && task?.completed) {
      updateData.completed = false;
      updateData.completedAt = null;
    }
    await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', id), updateData);
    dispatch({ type: 'SET_EDIT_TASK', payload: null });
  }, [allTasks, dispatch]);

  const handleAddTask = useCallback(async (e, toBottom = false) => {
    if (e && e.preventDefault) e.preventDefault();
    const s = stateRef.current;
    const textToSave = s.newTask.trim();
    if (!textToSave) return;
    
    const isHeaderToSave = !!s.newTaskIsHeader;

    dispatch({ type: 'SET_NEW_TASK', payload: '' });
    dispatch({ type: 'SET_NEW_TASK_HEADER', payload: false });

    const channelTasks = (allTasks || []).filter((t) => t.channelId === s.activeChannelId);
    const useParentId = toBottom ? null : s.selectedHeaderId;
    let newOrder = 0;
    if (useParentId) {
      const children = channelTasks
        .filter((t) => t.parentId === useParentId)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      newOrder =
        children.length > 0
          ? children[children.length - 1].order + 0.1
          : (channelTasks.find((t) => t.id === useParentId)?.order || 0) + 0.1;
    } else {
      newOrder = Math.ceil(channelTasks.reduce((max, t) => Math.max(max, t.order || 0), 0)) + 1;
    }
    await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks'), {
      text: textToSave,
      channelId: s.activeChannelId,
      parentId: useParentId || null,
      completed: false,
      isHeader: isHeaderToSave,
      createdAt: serverTimestamp(),
      order: newOrder,
      priority: 'High',
      dueDate: s.newTaskDueDate || null,
      taskDate: s.newTaskDueDate || null,
    });
  }, [allTasks, dispatch]);

  const handleAddPersonalTask = useCallback(async (e, toBottom = false) => {
    if (e && e.preventDefault) e.preventDefault();
    const s = stateRef.current;
    const textToSave = s.newTask.trim();
    if (!textToSave) return;
    
    dispatch({ type: 'SET_NEW_TASK', payload: '' });
    dispatch({ type: 'SET_NEW_TASK_HEADER', payload: false });

    const uid = authUser?.uid || 'test-uid-123';
    const channelId = `personal-${uid}`;

    let priority = 'High';
    if (s.selectedHeaderId?.includes('Medium')) priority = 'Medium';
    else if (s.selectedHeaderId?.includes('Low')) priority = 'Low';

    const pTasks = (allTasks || []).filter(
      (t) => t.channelId === channelId && t.priority === priority
    );

    let newOrder = 0;
    newOrder = Math.ceil(pTasks.reduce((max, t) => Math.max(max, t.order || 0), 0)) + 1;

    await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks'), {
      text: textToSave,
      channelId: channelId,
      parentId: null,
      completed: false,
      isHeader: false,
      createdAt: serverTimestamp(),
      order: newOrder,
      priority: priority,
      isStarred: false,
    });
  }, [allTasks, authUser?.uid, dispatch]);

  const moveToRoot = useCallback(async (id) => {
    await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', id), {
      parentId: null,
    });
  }, []);

  const handleSaveEvent = useCallback(async () => {
    const s = stateRef.current;
    const uid = authUser?.uid || 'test-uid-123';

    if (s.editingEventId === 'my-birthday') {
      const birthday = s.newEventStartDate ? s.newEventStartDate.split('-').slice(1).join('-') : null;
      await saveUserProfile({
        name: s.newEventPersonName,
        bio: s.newEventBio || null,
        motivationalMessage: s.newEventMotivation || null,
        birthYear: s.newEventBirthYear || null,
        birthday: birthday,
      });
      dispatch({ type: 'CLOSE_ADD_EVENT' });
      return;
    }

    if (s.newEventType === 'birthday' && !s.newEventPersonName.trim()) {
      alert("Please enter a person's name");
      return;
    }

    const allowsEmptyTitle = ['birthday', 'trip', 'event', 'tentative'].includes(s.newEventType);
    if (!allowsEmptyTitle && !s.newEventTitle.trim()) {
      alert('Please enter a title');
      return;
    }

    if (!s.newEventStartDate) {
      alert('Please select a date');
      return;
    }

    if (s.newEventType === 'trip' && !s.newTripCity) {
      alert('Please select a city for the trip');
      return;
    }

    // Auto-enable calendar filter
    const calendarIdMap = {
      event: 'nytm-event',
      tentative: 'nytm-tentative',
      birthday: 'nytm-birthday',
      task: 'nytm-tasks-all',
      concert: 'nytm-concert',
      venue: 'nytm-venue',
      restaurant: 'nytm-restaurant',
      sight: 'nytm-sight',
      shopping: 'nytm-shopping',
      holiday: 'nytm-holiday',
      habit: 'nytm-habit',
      flight: 'nytm-flight'
    };
    const targetCalId = calendarIdMap[s.newEventType];
    if (targetCalId && !(s.visibleCalendars || []).includes(targetCalId)) {
      const newList = [...(s.visibleCalendars || []), targetCalId];
      if (s.newEventType === 'task') {
        ['nytm-tasks-h', 'nytm-tasks-m', 'nytm-tasks-l'].forEach(p => {
          if (!newList.includes(p)) newList.push(p);
        });
      }
      dispatch({ type: 'SET_CALENDAR_VISIBILITY', payload: newList });
    }

    const adjustTime = (timeStr, hourShift) => {
      if (!timeStr) return null;
      const [h, m] = timeStr.split(':').map(Number);
      let totalMinutes = h * 60 + m + Math.round(hourShift * 60);
      totalMinutes = (totalMinutes + 1440) % 1440;
      const nh = Math.floor(totalMinutes / 60);
      const nm = Math.floor(totalMinutes % 60);
      return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
    };

    const isBirthday = s.newEventType === 'birthday';
    const isTrip = s.newEventType === 'trip';
    const isTentative = s.newEventType === 'tentative';
    const isTask = s.newEventType === 'task';
    const isHabit = s.newEventType === 'habit';

    const now = new Date();
    const todayId = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    let effectiveStartDate = normalizeDate(s.newEventStartDate);
    if (isTask && effectiveStartDate < todayId) {
      alert('Note: Tasks cannot be set in the past. This task will be set to Today.');
      effectiveStartDate = todayId;
    }

    const isProjectTask = s.editingEventChannelId && !s.editingEventChannelId.startsWith('personal-');

    const cleanId = String(s.editingEventId || '').replace(/-today$|-due$/, '');
    const originalItem = [...(events || []), ...(allTasks || []), ...(habits || [])].find(x => x.id === cleanId);
    const wasTask = originalItem ? (originalItem.type === 'task' || !!originalItem.channelId) : false;
    const wasHabit = originalItem ? originalItem.type === 'habit' : false;

    let resolvedTimezone = null;
    let resolvedCity = null;
    let resolvedLat = null;
    let resolvedLng = null;
    
    if (isTrip) {
      resolvedCity = s.newTripCity || null;
      resolvedTimezone = s.newTripTimezone || null;
      resolvedLat = s.newTripLat || null;
      resolvedLng = s.newTripLng || null;
    } else if (isTentative || s.newEventType === 'event') {
      const src = s.newEventTimezoneSource;
      if (src === 'home') {
        const hc = authProfile?.cities?.[0];
        if (hc) {
          resolvedCity = hc.name;
          resolvedTimezone = hc.timezone;
          resolvedLat = hc.lat;
          resolvedLng = hc.lng;
        }
      } else if (src === 'override') {
        resolvedCity = s.newEventOverrideCity || null;
        resolvedTimezone = s.newEventOverrideTimezone || null;
      } else if (src === 'place') {
        resolvedCity = s.newEventPlaceCity || null;
        resolvedTimezone = s.newEventPlaceTimezone || null;
      } else if (src?.startsWith('trip:')) {
        const tid = src.split(':')[1];
        const t = (events || []).find(x => x.id === tid);
        if (t) {
          resolvedCity = t.city;
          resolvedTimezone = t.timezone;
          resolvedLat = t.lat;
          resolvedLng = t.lng;
        }
      }
      
      // Fallback
      if (!resolvedTimezone) resolvedTimezone = s.newEventTimezone || null;
      if (!resolvedCity) resolvedCity = s.newEventCity || null;
      if (!resolvedLat) resolvedLat = s.newEventLat || null;
      if (!resolvedLng) resolvedLng = s.newEventLng || null;
    }

    const eventData = {
      title: isBirthday ? s.newEventPersonName : s.newEventTitle,
      personName: isBirthday ? s.newEventPersonName : null,
      birthYear: isBirthday ? s.newEventBirthYear : null,
      startDate: effectiveStartDate,
      start: effectiveStartDate,
      endDate: normalizeDate(s.newEventEndDate || effectiveStartDate),
      type: s.newEventType,
      isAllDay: isBirthday ? true : s.newEventIsAllDay,
      startTime: (isBirthday || s.newEventIsAllDay || isTask || isHabit) ? null : (s.newEventStartTime || null),
      endTime: (isBirthday || s.newEventIsAllDay || isTask || isHabit) ? null : (s.newEventEndTime || null),
      city: resolvedCity,
      timezone: resolvedTimezone,
      lat: resolvedLat,
      lng: resolvedLng,
      flightIn: isTrip ? (s.newTripFlightIn || []).filter(f => f.trim() !== '') : [],
      flightOut: isTrip ? (s.newTripFlightOut || []).filter(f => f.trim() !== '') : [],
      hotels: isTrip ? (s.newTripHotels || []) : [],
      flightData: isTrip ? (originalItem?.flightData || null) : null,
      venueName: (isHabit || isTrip || isTask) ? null : (s.newEventVenueName || null),
      address: (isHabit || isTrip || isTask) ? null : (s.newEventAddress || null),
      addressLat: (isHabit || isTrip || isTask) ? null : (s.newEventAddressLat || null),
      addressLng: (isHabit || isTrip || isTask) ? null : (s.newEventAddressLng || null),
      note: s.newEventNote || null,
      timezoneSource: (s.newEventType === 'event' || s.newEventType === 'tentative') && !s.newEventIsAllDay ? (s.newEventTimezoneSource || 'home') : 'home',
      isStarred: s.newEventIsStarred || false,
      createdBy: uid,
      updatedAt: serverTimestamp(),
    };

    const taskData = {
      text: s.newEventTitle,
      priority: isProjectTask ? (originalItem?.priority || 'High') : (s.newEventPriority || 'High'),
      dueDate: effectiveStartDate,
      taskDate: effectiveStartDate,
      venueName: null,
      address: null,
      addressLat: null,
      addressLng: null,
      note: s.newEventNote || null,
      completed: false,
      channelId: isProjectTask ? (originalItem?.channelId || s.editingEventChannelId) : `personal-${uid}`,
      createdBy: uid,
      updatedAt: serverTimestamp(),
      type: 'task'
    };

    const habitData = {
      title: s.newEventTitle,
      startDate: effectiveStartDate,
      endDate: s.newEventEndDate ? normalizeDate(s.newEventEndDate) : null,
      note: s.newEventNote || null,
      type: 'habit',
      updatedAt: serverTimestamp(),
    };

    if (originalItem) {
      taskData.completed = originalItem.completed || false;
      taskData.order = originalItem.order || 0;
    }

    if (isHabit) {
      if (s.editingEventId) {
        if (!wasHabit) {
          // Conversion to habit
          if (wasTask) {
            await safeDeleteDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', cleanId));
          } else {
            await safeDeleteDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events', cleanId));
          }
          await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'habits'), {
            ...habitData,
            createdBy: uid,
            createdAt: serverTimestamp(),
          });
        } else {
          // Update habit
          await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'habits', cleanId), habitData);
        }
      } else {
        // New habit
        await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'habits'), {
          ...habitData,
          createdBy: uid,
          createdAt: serverTimestamp(),
        });
      }
      dispatch({ type: 'CLOSE_ADD_EVENT' });
      return;
    }

    let eventId = cleanId;
    if (s.editingEventId) {
      if (wasHabit) {
        alert('Habits cannot be converted back to regular events or tasks.');
        return;
      }
      if (wasTask && !isTask) {
        // Task -> Event conversion
        await safeDeleteDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', cleanId));
        if (!eventData.isAllDay && eventData.startTime) {
          eventData.originalStartTime = eventData.startTime;
          eventData.originalEndTime = eventData.endTime;
        }
        const docRef = await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events'), { ...eventData, createdAt: serverTimestamp() });
        eventId = docRef.id;
      } else if (!wasTask && isTask) {
        // Event -> Task conversion
        await safeDeleteDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events', cleanId));
        await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks'), { ...taskData, createdAt: serverTimestamp() });
      } else if (isTask) {
        // Update Task
        await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', cleanId), taskData);
      } else {
        // Update Event
        if (!eventData.isAllDay && eventData.startTime) {
          const homeTz = authProfile?.cities?.[0]?.timezone || 'America/Chicago';
          const existing = (events || []).find((e) => e.id === cleanId);
          let origStart = existing?.originalStartTime;
          let origEnd = existing?.originalEndTime;

          let eventTz = homeTz;
          if (isTentative && eventData.timezone) {
            eventTz = eventData.timezone;
          } else if (eventData.timezoneSource.startsWith('trip:')) {
            const tripId = eventData.timezoneSource.split(':')[1];
            const trip = (events || []).find((e) => e.id === tripId);
            eventTz = trip?.timezone || homeTz;
          } else if (eventData.timezoneSource && eventData.timezoneSource !== 'home') {
            eventTz = eventData.timezoneSource;
          }

          if (eventTz && eventTz !== homeTz) {
            const eventOffset = getTimezoneOffsetHours(eventTz, eventData.startDate);
            const homeOffset = getTimezoneOffsetHours(homeTz, eventData.startDate);
            const shift = homeOffset - eventOffset;
            origStart = adjustTime(eventData.startTime, shift);
            origEnd = adjustTime(eventData.endTime, shift);
          } else {
            origStart = eventData.startTime;
            origEnd = eventData.endTime;
          }
          eventData.originalStartTime = origStart;
          eventData.originalEndTime = origEnd;
        }
        await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events', cleanId), eventData);
      }
    } else {
      // Create New
      if (isTask) {
        await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks'), { ...taskData, createdAt: serverTimestamp() });
      } else {
        if (!eventData.isAllDay && eventData.startTime) {
          eventData.originalStartTime = eventData.startTime;
          eventData.originalEndTime = eventData.endTime;
        }
        const docRef = await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events'), { ...eventData, createdAt: serverTimestamp() });
        eventId = docRef.id;
      }
    }

    const syncAllEventsToTrips = async (allEvents) => {
      const trips = allEvents.filter((e) => e.type === 'trip');
      const homeTz = authProfile?.cities?.[0]?.timezone || 'America/Chicago';

      let batch = safeWriteBatch(db);
      let count = 0;

      for (const event of allEvents) {
        if (event.type === 'task' || event.type === 'trip' || event.isAllDay) continue;
        if (!event.originalStartTime) continue;
        
        let eventTz = homeTz;
        if (event.timezoneSource?.startsWith('trip:')) {
          const tripId = event.timezoneSource.split(':')[1];
          const trip = trips.find((t) => t.id === tripId);
          eventTz = trip?.timezone || homeTz;
        } else if (event.timezoneSource && event.timezoneSource !== 'home') {
          eventTz = event.timezoneSource;
        }

        let targetTime = event.startTime;
        let targetEndTime = event.endTime;

        if (eventTz && eventTz !== homeTz) {
          const eventOffset = getTimezoneOffsetHours(eventTz, event.startDate);
          const homeOffset = getTimezoneOffsetHours(homeTz, event.startDate);
          const shift = eventOffset - homeOffset;
          targetTime = adjustTime(event.originalStartTime, shift);
          targetEndTime = adjustTime(event.originalEndTime, shift);
        }
        if (targetTime !== event.startTime || targetEndTime !== event.endTime) {
          batch.update(
            doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events', event.id),
            {
              startTime: targetTime,
              endTime: targetEndTime,
              updatedAt: serverTimestamp(),
            }
          );
          count++;
          if (count >= 450) {
            await batch.commit();
            batch = safeWriteBatch(db);
            count = 0;
          }
        }
      }
      if (count > 0) await batch.commit();
    };

    if (
      s.newEventType === 'trip' ||
      (events || []).find((e) => e.id === s.editingEventId)?.type === 'trip'
    ) {
      const currentEvents = (events || []).filter((e) => e.id !== eventId);
      await syncAllEventsToTrips([...currentEvents, { ...eventData, id: eventId }]);
    }

    dispatch({ type: 'CLOSE_ADD_EVENT' });
  }, [authUser?.uid, events, authProfile?.cities, allTasks, dispatch]);

  const handleRemoveFlight = useCallback(async (flight) => {
    if (!window.confirm(`Are you sure you want to remove flight ${flight.title} from this trip?`)) return;
    try {
      const trip = flight.trip;
      const newFlightIn = (trip.flightIn || []).filter(f => f !== flight.title);
      const newFlightOut = (trip.flightOut || []).filter(f => f !== flight.title);
      
      const newFlightData = { ...(trip.flightData || {}) };
      delete newFlightData[flight.title];

      await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events', trip.id), {
        flightIn: newFlightIn,
        flightOut: newFlightOut,
        flightData: newFlightData
      });
    } catch (e) {
      console.error('Error removing flight:', e);
      alert('Failed to remove flight');
    }
  }, []);

  const handleDeleteEvent = useCallback(async (id) => {
    if (window.confirm('Delete?')) {
      const cleanId = String(id).replace(/-today$|-due$/, '');
      const taskToDelete = (allTasks || []).find((t) => t.id === cleanId);
      const habitToDelete = (habits || []).find((h) => h.id === cleanId);

      if (taskToDelete) {
        await safeDeleteDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', cleanId));
        dispatch({ type: 'CLOSE_ADD_EVENT' });
        return;
      }
      if (habitToDelete) {
        await safeDeleteDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'habits', cleanId));
        dispatch({ type: 'CLOSE_ADD_EVENT' });
        return;
      }

      const eventToDelete = (events || []).find((e) => e.id === id);
      await safeDeleteDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events', id));
      if (eventToDelete?.type === 'trip') {
        const remainingEvents = (events || []).filter((e) => e.id !== id);
        const homeTz = authProfile?.cities?.[0]?.timezone;
        if (homeTz) {
          const batch = safeWriteBatch(db);
          let count = 0;
          const adjustTime = (timeStr, hourShift) => {
            if (!timeStr) return null;
            const [h, m] = timeStr.split(':').map(Number);
            let totalMinutes = h * 60 + m + Math.round(hourShift * 60);
            totalMinutes = (totalMinutes + 1440) % 1440;
            const nh = Math.floor(totalMinutes / 60);
            const nm = Math.floor(totalMinutes % 60);
            return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
          };
          remainingEvents.forEach((event) => {
            if (event.type === 'task' || event.type === 'trip' || event.isAllDay) return;
            if (!event.originalStartTime) return;
            const trip = remainingEvents.find(
              (t) =>
                t.type === 'trip' && event.startDate >= t.startDate && event.startDate <= t.endDate
            );
            let targetTime = event.originalStartTime;
            let targetEndTime = event.originalEndTime;
            if (trip && trip.timezone) {
              const tripOffset = getTimezoneOffsetHours(trip.timezone, event.startDate);
              const homeOffset = getTimezoneOffsetHours(homeTz, event.startDate);
              const shift = tripOffset - homeOffset;
              targetTime = adjustTime(event.originalStartTime, shift);
              targetEndTime = adjustTime(event.originalEndTime, shift);
            }
            if (targetTime !== event.startTime || targetEndTime !== event.endTime) {
              batch.update(
                doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events', event.id),
                {
                  startTime: targetTime,
                  endTime: targetEndTime,
                  updatedAt: serverTimestamp(),
                }
              );
              count++;
            }
          });
          if (count > 0) await batch.commit();
        }
      }
      dispatch({ type: 'CLOSE_ADD_EVENT' });
    }
  }, [events, allTasks, habits, authProfile?.cities, dispatch]);

  const handleDragEnd = useCallback(async (event) => {
    dispatch({ type: 'DRAG_END' });
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeType = active.data.current?.type;
    if (activeType === 'channel') {
      if (active.id === 'general_chat_global' || over.id === 'general_chat_global') return;
      const isStarredSort = active.data.current?.isStarredList;
      const activeId = String(active.id).replace(/^starred-/, '');
      const overId = String(over.id).replace(/^starred-/, '');
      if (isStarredSort) {
        const starred = (channels || [])
          .filter((c) => c.isStarred && c.type !== 'dm' && c.id !== 'general_chat_global')
          .sort((a, b) => (a.starredOrder || 0) - (b.starredOrder || 0));
        const oldIdx = starred.findIndex((c) => c.id === activeId);
        const newIdx = starred.findIndex((c) => c.id === overId);
        if (oldIdx === -1 || newIdx === -1) return;
        const reordered = arrayMove(starred, oldIdx, newIdx);
        const batch = safeWriteBatch(db);
        reordered.forEach((ch, idx) =>
          batch.update(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels', ch.id), {
            starredOrder: idx,
          })
        );
        await batch.commit();
      } else {
        const activeCh = (channels || []).find((c) => c.id === activeId);
        const overCh = (channels || []).find((c) => c.id === overId);
        if (!activeCh || !overCh || activeCh.parentId !== overCh.parentId) return;
        const siblings = (channels || [])
          .filter(
            (c) =>
              c.parentId === activeCh.parentId && c.type !== 'dm' && c.id !== 'general_chat_global'
          )
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        const oldIdx = siblings.findIndex((c) => c.id === activeId);
        const newIdx = siblings.findIndex((c) => c.id === overId);
        if (oldIdx === -1 || newIdx === -1) return;
        const reordered = arrayMove(siblings, oldIdx, newIdx);
        const batch = safeWriteBatch(db);
        reordered.forEach((ch, idx) =>
          batch.update(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels', ch.id), {
            order: idx,
          })
        );
        await batch.commit();
      }
    } else if (activeType === 'mobile-section') {
      const order = reconcileSectionOrder(safeJsonParse('mobileSectionOrder', null));
      const oldIdx = order.indexOf(active.id);
      const newIdx = order.indexOf(over.id);
      if (oldIdx !== -1 && newIdx !== -1) {
        const reordered = arrayMove(order, oldIdx, newIdx);
        localStorage.setItem('mobileSectionOrder', JSON.stringify(reordered));
        window.dispatchEvent(new Event('mobileSectionOrderChanged'));
      }
      return;
    } else if (activeType === 'task') {
      const draggedTask = active.data.current.task;
      const draggedFromDayId = active.data.current.draggedFromDayId;
      const overData = over.data.current;

      // CHECK IF DROPPED ON CALENDAR DAY
      let calendarDayId = null;
      if (overData?.type === 'day' && overData.dayId) {
        calendarDayId = overData.dayId;
      } else if (over.id && /^\d{4}-\d{2}-\d{2}$/.test(String(over.id))) {
        calendarDayId = String(over.id);
      }

      if (calendarDayId) {
        let activeIdStr = String(active.id);
        if (activeIdStr.endsWith('-today')) activeIdStr = activeIdStr.replace('-today', '');
        if (activeIdStr.endsWith('-due')) activeIdStr = activeIdStr.replace('-due', '');

        await safeUpdateDoc(
          doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', activeIdStr),
          {
            startDate: calendarDayId,
            dueDate: calendarDayId,
            updatedAt: serverTimestamp(),
          }
        );
        return;
      }

      const channelId = draggedTask.channelId;
      const isPersonal = channelId.startsWith('personal-');

      if (isPersonal) {
        const pTasks = (allTasks || []).filter((t) => t.channelId === channelId);
        const displayList = [];
        ['High', 'Medium', 'Low'].forEach((p) => {
          displayList.push({ id: `personal-folder-${p}`, isVirtualHeader: true, priority: p });
          const filtered = pTasks
            .filter((t) => t.priority === p)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          displayList.push(...filtered);
        });

        const oldIdx = displayList.findIndex((t) => t.id === active.id);
        const newIdx = displayList.findIndex((t) => t.id === over.id);

        if (oldIdx !== -1 && newIdx !== -1) {
          const temp = arrayMove(displayList, oldIdx, newIdx);
          const batch = safeWriteBatch(db);
          let currentPriority = 'High';
          let groupOrder = 0;
          let changedCount = 0;

          temp.forEach((item) => {
            if (item.isVirtualHeader) {
              currentPriority = item.priority;
              groupOrder = 0;
            } else {
              if (item.priority !== currentPriority || item.order !== groupOrder) {
                batch.update(
                  doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', item.id),
                  {
                    priority: currentPriority,
                    order: groupOrder,
                  }
                );
                changedCount++;
              }
              groupOrder++;
            }
          });
          if (changedCount > 0) await batch.commit();
        }
        return;
      }

      const allChannelTasks = (allTasks || [])
        .filter((t) => t.channelId === channelId)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const oldIdx = allChannelTasks.findIndex((t) => t.id === active.id);
      const newIdx = allChannelTasks.findIndex((t) => t.id === over.id);
      if (oldIdx !== -1 && newIdx !== -1) {
        let reorderedTasks = [...allChannelTasks];
        if (draggedTask.isHeader || (draggedTask.text || '').trim().endsWith(':')) {
          const children = allChannelTasks.filter((t) => t.parentId === draggedTask.id);
          const unitIds = [draggedTask.id, ...children.map((c) => c.id)];
          const unitItems = allChannelTasks.filter((t) => unitIds.includes(t.id));
          const overTask = allChannelTasks[newIdx];
          let anchorId = overTask.id;
          if (overTask.parentId) {
            const root = allChannelTasks.find((t) => t.id === overTask.parentId);
            if (root)
              anchorId =
                newIdx > oldIdx
                  ? allChannelTasks
                      .filter((t) => t.parentId === root.id)
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .pop().id
                  : root.id;
          }
          reorderedTasks = reorderedTasks.filter((t) => !unitIds.includes(t.id));
          let spliceIdx = reorderedTasks.findIndex((t) => t.id === anchorId);
          if (newIdx > oldIdx) spliceIdx += 1;
          reorderedTasks.splice(Math.max(0, spliceIdx), 0, ...unitItems);
          reorderedTasks = reorderedTasks.map((t) =>
            t.id === draggedTask.id ? { ...t, parentId: null } : t
          );
        } else {
          const temp = arrayMove(allChannelTasks, oldIdx, newIdx);
          const above = temp[newIdx - 1];
          const below = temp[newIdx + 1];

          let newParentId = null;
          if (above) {
            const aboveIsHeader = above.isHeader || (above.text || '').trim().endsWith(':');
            if (aboveIsHeader) {
              // Dropped immediately below a header -> goes into that folder
              newParentId = above.id;
            } else if (below) {
              const aboveParent = above.parentId || null;
              const belowIsHeader = below.isHeader || (below.text || '').trim().endsWith(':');
              const belowParent = belowIsHeader ? below.id : below.parentId || null;

              if (aboveParent && aboveParent === belowParent) {
                // Dropped squarely inside an existing folder bubble
                newParentId = aboveParent;
              } else if (
                aboveParent &&
                belowIsHeader &&
                aboveParent === (allChannelTasks.find((t) => t.id === below.id)?.parentId || null)
              ) {
                // Dropped between children of same parent header (if headers were nested, which they aren't currently but for safety)
                newParentId = aboveParent;
              } else {
                // Dropped at a boundary between folders or below the bubble
                newParentId = null;
              }
            } else {
              // Dropped at the very bottom of the entire list
              // Inherit parent from above to allow dropping at the bottom of a folder
              newParentId = above.parentId || null;
            }
          }

          reorderedTasks = temp.map((t) =>
            t.id === active.id ? { ...t, parentId: newParentId } : t
          );
        }
        const batch = safeWriteBatch(db);
        reorderedTasks.forEach((t, idx) =>
          batch.update(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', t.id), {
            order: idx,
            parentId: t.parentId || null,
          })
        );
        await batch.commit();
      }
    } else if (['event', 'concert', 'restaurant', 'sight', 'holiday', 'tentative'].includes(activeType)) {
      const overData = over.data.current;
      let newStartDate = null;

      if (overData?.type === 'day' && overData.dayId) {
        newStartDate = overData.dayId;
      } else if (over.id && /^\d{4}-\d{2}-\d{2}$/.test(String(over.id))) {
        newStartDate = String(over.id);
      } else if (overData?.item?.startDate) {
        newStartDate = overData.item.startDate;
      }

      if (newStartDate) {
        const item = active.data.current.item;
        if (item.startDate === newStartDate) return;

        const start = new Date(item.startDate + 'T12:00:00');
        const end = new Date(item.endDate + 'T12:00:00');
        const diff = end.getTime() - start.getTime();

        const nextStart = new Date(newStartDate + 'T12:00:00');
        const nextEnd = new Date(nextStart.getTime() + diff);
        const nextEndDateStr = nextEnd.toISOString().split('T')[0];

        // VALIDATE TIMEZONE
        if (!item.isAllDay) {
          const allTrips = (events || []).filter((e) => e.type === 'trip');
          const dateStr = newStartDate;
          const outTrips = allTrips.filter((t) => t.endDate === dateStr);
          const stayTrips = allTrips.filter((t) => t.startDate < dateStr && t.endDate > dateStr);
          const inTrips = allTrips.filter((t) => t.startDate === dateStr);

          const options = [];
          const seen = new Set();
          const addOption = (source) => {
            if (seen.has(source)) return;
            seen.add(source);
            options.push(source);
          };

          const isTravelDay = outTrips.length > 0 || inTrips.length > 0;
          if (isTravelDay) {
            outTrips.forEach((t) => addOption(`trip:${t.id}`));
            stayTrips.forEach((t) => addOption(`trip:${t.id}`));
            addOption('home');
            inTrips.forEach((t) => addOption(`trip:${t.id}`));
          } else {
            if (stayTrips.length > 0) stayTrips.forEach((t) => addOption(`trip:${t.id}`));
            else addOption('home');
          }

          // If only one option, auto-default to it
          if (options.length === 1) {
            await safeUpdateDoc(
              doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events', active.id),
              {
                startDate: newStartDate,
                start: newStartDate,
                endDate: nextEndDateStr,
                timezoneSource: options[0],
                updatedAt: serverTimestamp(),
              }
            );
            return;
          }

          // If multiple options but current is invalid, ask user
          if (!seen.has(item.timezoneSource)) {
            dispatch({
              type: 'OPEN_LOCATION_CONFLICT',
              payload: {
                id: active.id,
                startDate: newStartDate,
                endDate: nextEndDateStr,
                validOptions: options,
              },
            });
            return;
          }
        }

        await safeUpdateDoc(
          doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events', active.id),
          {
            startDate: newStartDate,
            start: newStartDate,
            endDate: nextEndDateStr,
            updatedAt: serverTimestamp(),
          }
        );
      }
    }
  }, [channels, allTasks, events, dispatch]);

  const handleMoveTaskPriority = useCallback(async (id, newPriority) => {
    const task = (allTasks || []).find((t) => t.id === id);
    if (!task) return;
    const channelTasks = (allTasks || []).filter(
      (t) => t.channelId === task.channelId && t.priority === newPriority && !t.completed
    );
    const minOrder = channelTasks.length > 0 ? Math.min(...channelTasks.map(t => t.order || 0)) : 0;
    const newOrder = minOrder - 1;
    await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks', id), {
      priority: newPriority,
      order: newOrder,
      completed: false,
      completedAt: null,
    });
  }, [allTasks]);

  const personalTasks = useMemo(
    () =>
      (allTasks || [])
        .filter((t) => t.channelId === `personal-${authUser?.uid || 'test-uid-123'}`)
        .sort((a, b) => (a.order || 0) - (b.order || 0)),
    [allTasks, authUser]
  );

  const effectiveActiveChannelId = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/p/')) {
      const parts = path.split('/');
      return parts[2] || 'general_chat_global';
    }
    return 'general_chat_global';
  }, [location.pathname]);

  const effectiveCurrentView = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/settings')) return 'settings';
    if (path.startsWith('/calendar')) return 'calendar';
    if (path.startsWith('/weather')) return 'weather';
    if (path.startsWith('/watch')) return 'watch';
    if (path.startsWith('/rss')) return 'rss';
    if (path.startsWith('/film-tv')) return 'film-tv';
    if (path.startsWith('/media')) return 'media';
    if (path.startsWith('/database')) return 'database';
    if (path.startsWith('/p/')) return 'workspace';
    return 'home';
  }, [location.pathname]);
  const handleSaveEditMessage = useCallback(async (id, text) => {
    if (!text.trim()) return;
    await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'messages', id), {
      text,
      editedAt: serverTimestamp(),
    });
    dispatch({ type: 'SET_EDIT_MESSAGE', payload: null });
  }, [dispatch]);

  const handleSendMessage = useCallback(async (text) => {
    if (!text.trim()) return;
    const cid = effectiveActiveChannelId;
    const uid = authUser?.uid || 'test-uid-123';
    await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'messages'), {
      text,
      channelId: cid,
      userId: uid,
      senderName: authProfile?.name || 'User',
      senderAvatar: authProfile?.avatar || '👤',
      createdAt: serverTimestamp(),
    });
    try {
      await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels', cid), {
        lastActivity: serverTimestamp(),
        lastActorId: uid,
      });
    } catch (e) {
      /* ignore */
    }
  }, [effectiveActiveChannelId, authUser?.uid, authProfile?.name, authProfile?.avatar]);

  useEffect(() => {
    if (!effectiveActiveChannelId) return;
    const q = query(
      collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'messages'),
      where('channelId', '==', effectiveActiveChannelId),
      orderBy('createdAt', 'desc'),
      limit(state.messageLimit)
    );
    const unsub = onSnapshot(q, (snap) => {
      const msgs = snap.docs.map((d) => ({ ...d.data(), id: d.id })).reverse();
      dispatch({ type: 'SET_MESSAGES', payload: msgs });
    });
    return () => unsub();
  }, [effectiveActiveChannelId, state.messageLimit]);

  useEffect(() => {
    if (!state.openDMChannelIds || state.openDMChannelIds.length === 0) return;

    const unsubs = state.openDMChannelIds.map((channelId) => {
      const q = query(
        collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'messages'),
        where('channelId', '==', channelId),
        orderBy('createdAt', 'desc'),
        limit(state.messageLimit)
      );
      return onSnapshot(q, (snap) => {
        const msgs = snap.docs.map((d) => ({ ...d.data(), id: d.id })).reverse();
        dispatch({ type: 'SET_DM_MESSAGES', payload: { channelId, messages: msgs } });
      });
    });

    return () => unsubs.forEach((unsub) => unsub());
  }, [state.openDMChannelIds, state.messageLimit]);

  const handleGoHome = useCallback(() => {
    navigate('/home' + window.location.search);
  }, [navigate]);
  const handleGoTikiBar = useCallback(() => {
    if (window.innerWidth < 1024) {
      // Force history: Home -> Chat
      navigate('/home', { replace: true });
      navigate('/chat');
      navigate('/p/general_chat_global/chat' + window.location.search);
    } else {
      navigate('/home' + window.location.search);
    }
  }, [navigate, dispatch]);
  const handleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    await signInWithPopup(auth, provider);
  };
  const handleTestSignIn = () => {
    authContext.dispatch({ type: 'SET_BYPASS' });
  };

  const handleSignOut = useCallback(async () => {
    await signOut(auth);
    authContext.dispatch({ type: 'LOGOUT' });
    dispatch({ type: 'LOGOUT' });
    navigate('/');
  }, [authContext, dispatch, navigate]);

  const tasks = useMemo(
    () =>
      (allTasks || [])
        .filter((t) => t.channelId === effectiveActiveChannelId)
        .sort((a, b) => (a.order || 0) - (b.order || 0)),
    [allTasks, effectiveActiveChannelId]
  );

  const pointerSensorOptions = useMemo(() => ({
    activationConstraint: {
      distance: 10,
      predicate: (e) => {
        if (e.nativeEvent.shiftKey) return false;
        if (e.nativeEvent.pointerType === 'touch' || e.nativeEvent.pointerType === 'pen')
          return false;
        if (e.target.closest('button') || e.target.closest('a') || e.target.closest('input'))
          return false;
        return true;
      },
    },
  }), []);

  const touchSensorOptions = useMemo(() => ({
    activationConstraint: { delay: 300, tolerance: 5 }
  }), []);

  const sensors = useSensors(
    useSensor(PointerSensor, pointerSensorOptions),
    useSensor(TouchSensor, touchSensorOptions),
    useSensor(KeyboardSensor)
  );

  const effectiveActiveChannel = (channels || []).find((c) => c.id === effectiveActiveChannelId);
  const activeColorHex = effectiveActiveChannel
    ? MUTED_COLORS.find((c) => c.id === effectiveActiveChannel.color)?.hex || '#ffffff'
    : '#ffffff';

  const projects = useMemo(
    () => (channels || []).filter((c) => c.type !== 'dm' && c.id !== 'general_chat_global'),
    [channels]
  );
  const dms = useMemo(() => (channels || []).filter((c) => c.type === 'dm'), [channels]);

  useEffect(() => {
    if (authProfile?.cities?.[0] && !authProfile.cities[0].timezone) {
      const city = authProfile.cities[0];
      if (import.meta.env.DEV) console.log('[DEBUG] Missing timezone for city:', city.name);
      fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city.name)}&count=1&language=en&format=json`
      )
        .then((res) => res.json())
        .then((data) => {
          const tz = data.results?.[0]?.timezone || 'UTC';
          if (import.meta.env.DEV) console.log('[DEBUG] Fetched timezone:', tz, 'for', city.name);
          if (tz !== authProfile.cities[0].timezone) {
            const newCities = [...authProfile.cities];
            newCities[0] = { ...newCities[0], timezone: tz };
            saveUserProfile({ cities: newCities });
          }
        })
        .catch(console.error);
    }
  }, [authProfile?.cities, saveUserProfile]);

  useEffect(() => {
    if (!authUser || isVault) return;
    
    const performHeartbeat = () => {
      const now = Date.now();
      let last = 0;
      try {
        last = parseInt(localStorage.getItem('lastHeartbeat_v2') || '0', 10);
      } catch (e) {
        console.warn('localStorage failed, falling back to window lock');
      }
      
      // Fallback to in-memory lock if localStorage is failing silently
      const memLast = window.__lastHeartbeat_v2 || 0;
      const effectiveLast = Math.max(last, memLast);

      if (now - effectiveLast > 150000) { // 2.5 minutes lock
        window.__lastHeartbeat_v2 = now;
        try {
          localStorage.setItem('lastHeartbeat_v2', now.toString());
        } catch (e) {
          // ignore
        }
        saveUserProfile({ lastSeen: serverTimestamp() });
      }
    };

    // Fire immediately if needed, then run interval
    performHeartbeat();
    const interval = setInterval(performHeartbeat, 3 * 60 * 1000); // 3 minutes
    
    return () => clearInterval(interval);
  }, [authUser?.uid, isVault, saveUserProfile]);

  useEffect(() => {
    if (!effectiveUser || dataLoading || isVault) return;
    
    const lastBackupStr = localStorage.getItem('last_auto_backup_v1');
    const lastBackup = parseInt(lastBackupStr || '0', 10);
    const now = Date.now();
    const TWO_WEEKS = 14 * 24 * 60 * 60 * 1000;
    
    // Safety check to ensure we actually have data loaded before backing up empty arrays
    if (events.length === 0 && allTasks.length === 0) return;
    
    if (now - lastBackup > TWO_WEEKS) {
      if (backupInProgressRef.current) return;
      backupInProgressRef.current = true;
      localStorage.setItem('last_auto_backup_v1', now.toString());

      const doBackup = async () => {
        if (import.meta.env.DEV) console.log('[BACKUP] Starting automated database backup...');
        try {
          const { doc, collection, serverTimestamp, setDoc } = await import('firebase/firestore');
          const { db } = await import('./firebase');
          const { appId: APP_ID_CONSTANT } = await import('./constants');
          
          const backupId = `backup_${now}`;
          const backupData = {
            events: events || [],
            tasks: allTasks || [],
            habits: habits || [],
            habitLogs: habitLogs || [],
          };
          
          let chunkIndex = 0;
          let currentBatch = safeWriteBatch(db);
          let currentBatchCount = 0;
          
          const saveBatchIfNeeded = async (force = false) => {
            if (currentBatchCount >= 450 || (force && currentBatchCount > 0)) {
              await currentBatch.commit();
              currentBatch = safeWriteBatch(db);
              currentBatchCount = 0;
            }
          };

          for (const [collectionName, items] of Object.entries(backupData)) {
            const chunkSize = 500;
            for (let i = 0; i < items.length; i += chunkSize) {
              const chunk = items.slice(i, i + chunkSize);
              const chunkDocRef = doc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'calendar_backups'));
              currentBatch.set(chunkDocRef, {
                backupId,
                userId: effectiveUser.uid,
                createdAt: serverTimestamp(),
                collection: collectionName,
                chunkIndex: chunkIndex++,
                data: JSON.stringify(chunk)
              });
              currentBatchCount++;
              await saveBatchIfNeeded();
            }
          }
          await saveBatchIfNeeded(true);
          
          // Write metadata document
          const metaDocRef = doc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'calendar_backups_metadata'));
          await safeSetDoc(metaDocRef, {
            backupId,
            userId: effectiveUser.uid,
            createdAt: serverTimestamp(),
            totalChunks: chunkIndex,
          });
          
          if (import.meta.env.DEV) console.log('[BACKUP] Automated backup complete.', backupId);
        } catch (error) {
          console.error('[BACKUP] Backup failed:', error);
          if (lastBackupStr) {
            localStorage.setItem('last_auto_backup_v1', lastBackupStr);
          } else {
            localStorage.removeItem('last_auto_backup_v1');
          }
        } finally {
          backupInProgressRef.current = false;
        }
      };
      
      doBackup();
    }
  }, [effectiveUser, dataLoading, events, allTasks, habits, habitLogs, isVault]);

  useEffect(() => {
    if (
      state.isAddingEvent &&
      state.newEventStartDate &&
      !state.editingEventId &&
      state.newEventType !== 'trip'
    ) {
      const dateStr = state.newEventStartDate;
      const allTrips = (events || []).filter((e) => e.type === 'trip');
      const outTrips = allTrips.filter((t) => t.endDate === dateStr);
      const stayTrips = allTrips.filter((t) => t.startDate < dateStr && t.endDate > dateStr);
      const inTrips = allTrips.filter((t) => t.startDate === dateStr);

      const homeCity = authProfile?.cities?.[0];
      
      let defaultSrc = 'home';
      if (outTrips.length > 0) {
        defaultSrc = `trip:${outTrips[0].id}`;
      } else if (stayTrips.length > 0) {
        defaultSrc = `trip:${stayTrips[0].id}`;
      } else if (homeCity) {
        defaultSrc = 'home';
      } else if (inTrips.length > 0) {
        defaultSrc = `trip:${inTrips[0].id}`;
      }

      // AUTO-SNAP LOGIC:
      // If we have a custom location set, check if it matches a current trip city.
      // If so, snap to the trip and turn off custom location.
      if (state.newEventTimezoneSource === 'override' && state.newEventOverrideCity) {
        const cleanCity = (name) => name?.split(',')[0].trim().toLowerCase() || '';
        const currentCustom = cleanCity(state.newEventOverrideCity);
        const matchingTrip = allTrips.find(t => 
          (t.startDate === dateStr || t.endDate === dateStr || (t.startDate < dateStr && t.endDate > dateStr)) &&
          cleanCity(t.city) === currentCustom
        );

        if (matchingTrip) {
          dispatch({ 
            type: 'SET_NEW_EVENT_INPUT', 
            payload: { 
              newEventOverrideCity: '',
              newEventOverrideTimezone: '',
              newEventTimezoneSource: `trip:${matchingTrip.id}` 
            } 
          });
          return; // Exit so we don't overwrite with default below
        }
      }

      if (state.newEventTimezoneSource !== defaultSrc) {
        dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: { newEventTimezoneSource: defaultSrc } });
      }
    }
  }, [
    state.isAddingEvent,
    state.newEventStartDate,
    state.editingEventId,
    state.newEventType,
    events,
    authProfile?.cities,
  ]);

  const calendarItems = useMemo(() => {
    const items = [];
    const visibleList = state.visibleCalendars || [
      'nytm-event',
      'nytm-tasks-all',
      'nytm-tasks-starred',
      'nytm-tasks-h',
      'nytm-tasks-m',
      'nytm-tasks-l',
      'nytm-birthday',
      'nytm-holiday',
      'nytm-google-calendar',
    ];

    if (events) {
      events.forEach((e) => {
        const start = normalizeDate(e.startDate || e.date);
        if (start) {
          const type = e.type || 'event';
          if (type === 'trip' || visibleList.includes(`nytm-${type}`)) {
            let title = e.title || e.name || '';

            items.push({
              // eslint-disable-next-line react-hooks/purity
              id: e.id || Math.random().toString(),
              type,
              ...e,
              startDate: start,
              endDate: normalizeDate(e.endDate) || start,
              title,
            });
          }
        }
      });
    }

    if (visibleList?.includes('nytm-photo') && photos) {
      photos.forEach(p => {
        if (p.exif && p.exif.DateTimeOriginal && p.lat && p.lng) {
          const start = p.exif.DateTimeOriginal.substring(0, 10);
          items.push({
            ...p,
            id: p.id,
            type: 'photo',
            startDate: start,
            endDate: start,
            title: p.name || 'Photo',
            lat: p.lat,
            lng: p.lng,
          });
        }
      });
    }

    const personalChannelId = `personal-${authUser?.uid || 'test-uid-123'}`;

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    (allTasks || []).forEach((t) => {
      const isPersonal = t.channelId === personalChannelId;
      const isHeader = t.isHeader || (t.text || '').trim().endsWith(':');
      
      // Do not render task headers/folders or tasks from the global chat on the calendar
      if (isHeader || t.channelId === 'general_chat_global') return;

      const priority = t.priority;
      if (!priority || !['High', 'Medium', 'Low'].includes(priority)) return;

      const priorityKey = `nytm-tasks-${priority.toLowerCase()[0]}`;
      if (!visibleList.includes(priorityKey)) return;
      
      // Enforce the PRJ toggle for project-level tasks
      if (!isPersonal && !visibleList.includes('nytm-tasks-prj')) return;

      const projectName = isPersonal ? '' : (channels || []).find(c => c.id === t.channelId)?.name || 'Project';
      const displayTitle = isPersonal ? t.text : `${t.text} (${projectName})`;

      if (t.completed) {
        if (!state.showCompletedTasks) return;
        const due = normalizeDate(t.dueDate);
        const dateStr = due || todayStr;
        
        items.push({
          ...t,
          id: `${t.id}-completed`,
          originalId: t.id,
          type: 'task',
          startDate: dateStr,
          endDate: dateStr,
          startTime: null,
          title: displayTitle,
          isHeader,
          isProjectTask: !isPersonal,
        });
        return;
      }

      const due = normalizeDate(t.dueDate);
      const hasDueToday = due === todayStr;

      // Always show on today (rollover)
      items.push({
        ...t,
        id: `${t.id}-today`,
        originalId: t.id,
        type: 'task',
        startDate: todayStr,
        endDate: todayStr,
        startTime: null,
        title: displayTitle,
        isHeader,
        priority,
        isRollover: !hasDueToday,
        isProjectTask: !isPersonal,
      });

      // Also show on due date if it's set and in the future
      if (due && due > todayStr) {
        items.push({
          ...t,
          id: `${t.id}-due`,
          originalId: t.id,
          type: 'task',
          startDate: due,
          endDate: due,
          startTime: null,
          title: displayTitle,
          isHeader,
          priority,
          isProjectTask: !isPersonal,
        });
      }
    });

    if (visibleList.includes('nytm-holiday')) {
      const currentYear = new Date().getFullYear();
      for (let year = currentYear - 1; year <= currentYear + 3; year++) {
        items.push(...getUSHolidays(year).map((h) => ({ ...h, isHoliday: true })));
      }
    }

    if (visibleList.includes('nytm-google-calendar') && state.googleEvents) {
      items.push(...state.googleEvents);
    }

    // FINAL HIERARCHICAL SORTING
    const getSortPriority = (item) => {
      if (item.type === 'trip') return 0;
      if (item.isHoliday) return 1;
      if (item.type === 'google-event') return 2; // Treat google events like events
      if (item.type === 'event' && item.isAllDay !== false) return 2;
      if (item.type === 'event') return 3;
      if (item.type === 'birthday') return 4;
      if (item.type === 'task') {
        const priorityMap = { High: 5, Medium: 6, Low: 7 };
        return priorityMap[item.priority] || 5;
      }
      if (['concert', 'restaurant', 'sight'].includes(item.type)) return 8;
      if (item.type === 'tentative' && item.isAllDay !== false) return 10;
      if (item.type === 'tentative') return 11;
      return 12;
    };

    items.sort((a, b) => {
      const pA = getSortPriority(a);
      const pB = getSortPriority(b);
      if (pA !== pB) return pA - pB;

      // Same type sorting
      if (a.type === 'task' && b.type === 'task') {
        const parentA = a.parentId || a.id;
        const parentB = b.parentId || b.id;

        if (parentA !== parentB) {
          // Different folders: sort by folder order
          const folderA = (allTasks || []).find((t) => t.id === parentA) || a;
          const folderB = (allTasks || []).find((t) => t.id === parentB) || b;
          return (folderA.order || 0) - (folderB.order || 0);
        }
        // Same folder: header first, then children by order
        if (a.id === parentA) return -1;
        if (b.id === parentB) return 1;
        return (a.order || 0) - (b.order || 0);
      }

      if (
        ['event', 'concert', 'restaurant', 'sight', 'tentative', 'birthday', 'google-event'].includes(a.type)
      ) {
        const timeA = a.startTime || '00:00';
        const timeB = b.startTime || '00:00';
        return timeA.localeCompare(timeB);
      }

      return 0;
    });

    // Handle Collapsed State (filter out children of collapsed headers)
    const filteredItems = items.filter((item) => {
      if (item.type === 'task' && item.parentId) {
        return !state.collapsedHeaderIds[item.parentId];
      }
      return true;
    });

    // Add displayTime with asterisk logic for travel days
    filteredItems.forEach((item) => {
      if (item.type === 'task' || item.type === 'trip' || item.isAllDay || !item.startTime) {
        item.displayTime = item.startTime;
        return;
      }
      const trip = items.find(
        (t) => t.type === 'trip' && (item.startDate === t.startDate || item.startDate === t.endDate)
      );
      if (trip) {
        if (item.startDate === trip.startDate && item.timezoneSource === 'home') {
          item.displayTime = item.startTime + '*';
        } else if (item.startDate === trip.endDate && item.timezoneSource === 'trip') {
          item.displayTime = item.startTime + '*';
        } else {
          item.displayTime = item.startTime;
        }
      } else {
        item.displayTime = item.startTime;
      }
    });

    return filteredItems;
  }, [
    events,
    channels,
    allTasks,
    state.visibleCalendars,
    authProfile?.cities,
    state.collapsedHeaderIds,
    state.googleEvents,
    photos,
  ]);

  const handleDragStart = useCallback((e) => {
    let title = 'Item';
    const type = e.active.data.current?.type;
    if (type === 'task') {
      title = e.active.data.current?.task?.text || 'Task';
    } else if (type === 'channel') {
      title = e.active.data.current?.channel?.name || 'Channel';
    } else if (type === 'mobile-section') {
      const sectionNames = {
        'projects': 'Projects',
        'bookmarks': 'Bookmarks',
        'wantToGo': 'Want to Go',
        'toWatch': 'To Watch',
        'todayEvents': 'Events',
        'habits': 'Habits',
        'myTasks': 'My Tasks',
        'groceryList': 'Grocery List',
        'internetRadio': 'Internet Radio',
        'weather': 'Weather',
      };
      title = sectionNames[e.active.id] || 'Section';
    } else if (e.active.data.current?.item) {
      title = e.active.data.current?.item?.title || e.active.data.current?.item?.name || 'Event';
    }

    dispatch({
      type: 'DRAG_START',
      payload: { id: e.active.id, type, title },
    });
  }, [dispatch]);

  const handleGoChat = useCallback(() => {
    dispatch({ type: 'SET_VIEW', payload: 'home' });
    navigate('/home', { replace: true });
    navigate('/chat' + window.location.search);
  }, [dispatch, navigate]);

  const headerClocks = useMemo(() => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const homeCity = authProfile?.cities?.[0] || {
      name: 'Dallas, TX',
      timezone: 'America/Chicago',
      lat: 32.7767,
      lng: -96.797,
    };
    
    const allTrips = (events || []).filter((e) => e.type === 'trip');
    const outTrips = allTrips.filter((e) => e.endDate === todayStr);
    const inTrips = allTrips.filter((e) => e.startDate === todayStr);
    const ongoingTrips = allTrips.filter((e) => e.startDate < todayStr && e.endDate > todayStr);

    const sequence = [];
    
    // 1. Home City (Always First)
    if (homeCity) {
      sequence.push({
        city: homeCity.name,
        timezone: homeCity.timezone || 'UTC',
        lat: homeCity.lat,
        lng: homeCity.lng
      });
    }

    // 2. Departure City (Trip ending today)
    outTrips.forEach((t) => {
      sequence.push({
        city: t.city || t.title || 'Trip',
        timezone: t.timezone || 'UTC',
        lat: t.lat,
        lng: t.lng
      });
    });

    // 3. Current/Ongoing City (Staying there)
    if (outTrips.length === 0 && inTrips.length === 0) {
      ongoingTrips.forEach((t) => {
        sequence.push({
          city: t.city || t.title || 'Trip',
          timezone: t.timezone || 'UTC',
          lat: t.lat,
          lng: t.lng
        });
      });
    }

    // 4. Arrival City (Trip starting today)
    inTrips.forEach((t) => {
      sequence.push({
        city: t.city || t.title || 'Trip',
        timezone: t.timezone || 'UTC',
        lat: t.lat,
        lng: t.lng
      });
    });

    // Filter out duplicates (based on city name)
    const unique = [];
    const seen = new Set();
    sequence.forEach(c => {
      const rawCity = c.city || 'Unknown';
      const cityName = rawCity.split(',')[0].trim().toUpperCase();
      if (cityName && !seen.has(cityName)) {
        unique.push({ ...c, city: rawCity });
        seen.add(cityName);
      }
    });

    return unique;
  }, [authProfile?.cities, events]);

  // Stable timestamp for online status check, updates every 30 seconds
  const [nowTimestamp, setNowTimestamp] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNowTimestamp(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const onlineThreshold = 300000;

  useEffect(() => {
    if (!authUser || !authProfile?.googleCalendarConnected) return;

    const fetchCalendar = async () => {
      try {
        const { appId: APP_ID_CONSTANT } = await import('./constants');
        const { useEmulator } = await import('./firebase');
        
        const url = useEmulator
          ? `http://127.0.0.1:5001/${APP_ID_CONSTANT}/us-central1/fetchGoogleCalendar`
          : `https://us-central1-${APP_ID_CONSTANT}.cloudfunctions.net/fetchGoogleCalendar`;
        const res = await authedFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: authUser.uid, calendarIds: authProfile?.googleCalendarSelectedIds })
        });
        
        if (!res.ok) {
          if (res.status === 401) {
             console.warn("Google Calendar token expired. Disconnecting.");
             saveUserProfile({ googleCalendarConnected: false, googleCalendarEmail: null });
             dispatch({ type: 'SET_GOOGLE_EVENTS', payload: [] });
             dispatch({ type: 'SET_GOOGLE_CALENDARS', payload: [] });
             return;
          }
          throw new Error('Failed to fetch google calendar');
        }
        const data = await res.json();
        
        if (data.events) {
          const parsedEvents = data.events.map(ev => {
             const calObj = data.calendars?.find(c => c.id === ev.calendarId);
             const isAllDay = !!ev.start?.date;
             let rawEndDate = ev.end?.date || ev.end?.dateTime?.split('T')[0];
             if (isAllDay && rawEndDate) {
                 const d = new Date(`${rawEndDate}T12:00:00Z`);
                 d.setUTCDate(d.getUTCDate() - 1);
                 rawEndDate = d.toISOString().split('T')[0];
             }
             return {
                 id: ev.id,
                 title: ev.summary || '(No title)',
                 date: ev.start?.date || ev.start?.dateTime?.split('T')[0],
                 startDate: ev.start?.date || ev.start?.dateTime?.split('T')[0],
                 endDate: rawEndDate,
                 startTime: ev.start?.dateTime ? ev.start.dateTime.split('T')[1].substring(0,5) : null,
                 endTime: ev.end?.dateTime ? ev.end.dateTime.split('T')[1].substring(0,5) : null,
                 isAllDay: isAllDay,
                 type: 'google-event',
                 location: ev.location,
                 description: ev.description,
                 isReadOnly: true,
                 calendarId: ev.calendarId,
                 color: calObj?.backgroundColor || '#10b981',
                 calendarName: calObj?.summary || 'Google Calendar'
             };
          });
          dispatch({ type: 'SET_GOOGLE_EVENTS', payload: parsedEvents });
        }
        if (data.calendars) {
           dispatch({ type: 'SET_GOOGLE_CALENDARS', payload: data.calendars });
        }
      } catch (err) {
        console.error("Google Calendar Fetch Error:", err);
      }
    };
    
    fetchCalendar();
    
  }, [authUser, authProfile?.googleCalendarConnected, JSON.stringify(authProfile?.googleCalendarSelectedIds)]);

  const layoutProps = useMemo(
    () => ({
      state,
      dispatch,
      sensors,
      handleDragStart,
      handleDragEnd,
      handleAddTask,
      handleAddPersonalTask,
      handleMoveTaskPriority,
      handleSendMessage,
      handleSaveEditMessage,
      toggleTask,
      toggleTaskStar,
      handleSaveEditTask,
      deleteTask,
      handleRemoveFlight,
      moveToRoot,
      handleSaveEvent,
      handleDeleteEvent,
      taskInputRef,
      taskEditRef,
      chatInputRef,
      notepadRef,
      noteTimeoutRef,
      isLocalEditRef,
      personalTasks,
      tasks,
      activeUploads: state.activeUploads,
      allTasks: allTasks || [],
      editingTaskId: state.editingTaskId,
      editTaskText: state.editTaskText,
      editTaskDueDate: state.editTaskDueDate,
      editTaskVenueName: state.editTaskVenueName,
      editTaskAddress: state.editTaskAddress,
      editTaskAddressLat: state.editTaskAddressLat,
      editTaskAddressLng: state.editTaskAddressLng,
      channels: channels || [],
      allUsers: allUsers || [],
      habits: habits || [],
      b2Token,
      habitLogs: habitLogs || [],
      onlineUsers: (allUsers || []).filter((u) => {
        if (!u) return false;
        const name = (u.name || '').toLowerCase();
        if (name.includes('ghost') || name.includes('tester') || name.startsWith('user '))
          return false;
        return nowTimestamp - (u.lastSeen?.seconds * 1000 || 0) < onlineThreshold;
      }),
      offlineUsers: (allUsers || []).filter((u) => {
        if (!u) return false;
        const name = (u.name || '').toLowerCase();
        if (name.includes('ghost') || name.includes('tester') || name.startsWith('user '))
          return false;
        return nowTimestamp - (u.lastSeen?.seconds * 1000 || 0) >= onlineThreshold;
      }),
      reactionFilter: state.reactionFilter,
      messages: state.messages || [],
      events: events || [],
      photos: photos || [],
      audioTracks: audioTracks || [],
      headerClocks,
      chatInputHtml: state.chatInputHtml,
      getDmUser: (channel) =>
        getDmUser(channel, authUser || { uid: 'test-uid-123' }, allUsers || []),
      getProjectPath,
      getRootProject,
      activeItem: null,
      activeChannel: effectiveActiveChannel,
      isDM:
        (channels || []).find((c) => c.id === (state.chatChannelId || state.activeChannelId))
          ?.type === 'dm',
      activeColorHex,
      handleSignOut,
      handleGoHome,
      handleGoTikiBar,
      isVault,
      navigate,
      handleGoChat,
      saveUserProfile,
      username:
        (allUsers || []).find((u) => u.id === (authUser?.uid || 'test-uid-123'))?.name ||
        authProfile?.name ||
        (isBypass ? 'Test User' : 'User'),
      bio:
        (allUsers || []).find((u) => u.id === (authUser?.uid || 'test-uid-123'))?.bio ||
        authProfile?.bio,
      motivationalMessage:
        (allUsers || []).find((u) => u.id === (authUser?.uid || 'test-uid-123'))
          ?.motivationalMessage ||
        authProfile?.motivationalMessage ||
        (isBypass ? 'Test Mode Active' : ''),
      userAvatar:
        (allUsers || []).find((u) => u.id === (authUser?.uid || 'test-uid-123'))?.avatar ||
        authProfile?.avatar ||
        (isBypass ? '🤖' : '👤'),
      handleCreateChannel: async (nameArg, colorArg, parentIdArg) => {
        const n = (typeof nameArg === 'string' ? nameArg : state.newChannelName) || '';
        const c = (typeof colorArg === 'string' ? colorArg : state.newChannelColor) || 'default';
        const p =
          (typeof parentIdArg === 'string' ? parentIdArg : state.newChannelParentId) || null;
        if (!n.trim()) return;
        const targetUid = authUser?.uid || 'test-uid-123';
        const docRef = await safeAddDoc(
          collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels'),
          {
            name: n.toLowerCase(),
            createdBy: targetUid,
            createdAt: serverTimestamp(),
            order: Date.now(),
            color: c,
            parentId: p,
            participants: [targetUid],
          }
        );
        dispatch({ type: 'CLOSE_CREATE_CHANNEL' });
        return docRef.id;
      },
      linkify,
      toggleProjectStar: async (ch) => {
        if (isBypass && !isVault) return;
        await safeUpdateDoc(
          doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels', ch.id),
          { isStarred: !ch.isStarred }
        );
      },
      handleDeleteChannel: async (id) => {
        if (isBypass && !isVault) return;
        if (window.confirm('Move this project to the trash?')) {
          const ch = (channels || []).find((c) => c.id === id);
          if (ch) {
            try {
              await safeSetDoc(
                doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'deleted_channels', id),
                { ...ch, deletedAt: serverTimestamp() }
              );
              await safeDeleteDoc(
                doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels', id)
              );
              dispatch({ type: 'CLOSE_EDIT_CHANNEL' });
            } catch (err) {
              console.error('Delete failed:', err);
            }
          }
        }
      },
      handleRestoreChannel: async (ch) => {
        if (isBypass && !isVault) return;
        const restoreId = ch._originalId || ch.id;
        const { deletedAt, id, _originalId, ...cleanData } = ch;
        try {
          await safeSetDoc(
            doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels', restoreId),
            { ...cleanData, id: restoreId }
          );
          await safeDeleteDoc(
            doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'deleted_channels', ch.id)
          );
        } catch (e) {
          console.error(e);
        }
      },
      handlePurgeChannel: async (id) => {
        if (isBypass && !isVault) return;
        if (window.confirm('Permanently delete?'))
          await safeDeleteDoc(
            doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'deleted_channels', id)
          );
      },
      handleSaveNote: async (noteId, html, sessionId) => {
        if (!noteId) return;
        try {
          const updateData = {
            content: html,
            updatedAt: serverTimestamp(),
            updatedBy: authUser?.uid || 'test-uid-123',
          };
          if (sessionId) {
            updateData.updatedBySession = sessionId;
          }
          await safeSetDoc(
            doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'notes', noteId),
            updateData,
            { merge: true }
          );
        } catch (err) {
          console.error('Auto-save failed:', err);
        }
      },
      handleSaveHabit: async (habit) => {
        const uid = authUser?.uid || 'test-uid-123';
        if (habit.id) {
          await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'habits', habit.id), {
            ...habit,
            updatedAt: serverTimestamp(),
          });
        } else {
          await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'habits'), {
            ...habit,
            createdBy: uid,
            createdAt: serverTimestamp(),
          });
        }
      },
      handleDeleteHabit: async (id) => {
        if (window.confirm('Delete this habit?')) {
          await safeDeleteDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'habits', id));
        }
      },
      handleLogHabit: async (monthKey, dayId, habitId, logData, bypassWarning = false) => {
        if (!bypassWarning && logData?.completed) {
          const currentHour = new Date().getHours();
          if (currentHour >= 0 && currentHour < 5) {
            setLateNightHabitWarning({ monthKey, dayId, habitId, logData });
            return;
          }
        }
        
        const uid = authUser?.uid || 'test-uid-123';
        const docId = `${uid}_${monthKey}`;
        const docRef = doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'habitLogs', docId);
        
        const existing = (habitLogs || []).find(l => l.id === docId);
        const nextData = {
          uid,
          monthKey,
          days: {
            ...(existing?.days || {}),
            [dayId]: {
              ...(existing?.days?.[dayId] || {}),
              [habitId]: {
                ...logData,
                updatedAt: new Date().toISOString()
              }
            }
          }
        };

        if (existing) {
          await safeUpdateDoc(docRef, nextData);
        } else {
          await safeSetDoc(docRef, nextData);
        }
      },
      handleImportProject: async (fileOrData) => {
        dispatch({ type: 'SET_IMPORTING', payload: true });
        const processImport = async (data) => {
          try {
            const uid = authUser?.uid || 'test-uid-123';
            const { deletedAt, id, ...cleanChannel } = data.channel;
            const chanRef = await safeAddDoc(
              collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels'),
              {
                ...cleanChannel,
                createdBy: uid,
                createdAt: serverTimestamp(),
                participants: [uid],
                isStarred: false,
                order: Date.now(),
              }
            );
            if (data.tasks) {
              const batch = safeWriteBatch(db);
              data.tasks.forEach((t) => {
                const { id: oldTaskId, ...cleanTask } = t;
                batch.set(
                  doc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks')),
                  { ...cleanTask, channelId: chanRef.id, createdAt: serverTimestamp() }
                );
              });
              await batch.commit();
            }
            if (data.messages) {
              const batch = safeWriteBatch(db);
              data.messages.forEach((m) => {
                const { id: oldMsgId, ...cleanMsg } = m;
                batch.set(
                  doc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'messages')),
                  {
                    ...cleanMsg,
                    channelId: chanRef.id,
                    createdAt: cleanMsg.createdAt || serverTimestamp(),
                  }
                );
              });
              await batch.commit();
            }
            if (data.note) {
              const { id: oldNoteId, ...cleanNote } = data.note;
              await safeAddDoc(
                collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'notes'),
                { ...cleanNote, channelId: chanRef.id, updatedAt: serverTimestamp() }
              );
            }
            alert(`Imported: ${data.channel.name}`);
          } catch (err) {
            alert('Import failed: ' + err.message);
          }
          dispatch({ type: 'SET_IMPORTING', payload: false });
        };
        if (fileOrData instanceof File) {
          const reader = new FileReader();
          reader.onload = (e) => processImport(JSON.parse(e.target.result));
          reader.readAsText(fileOrData);
        } else await processImport(fileOrData);
      },
      handleConfirmEditChannel: async () => {
        if (state.editingChannelId) {
          const cleanId = String(state.editingChannelId).replace(/^starred:/, '');
          await safeUpdateDoc(
            doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels', cleanId),
            {
              name: (state.editChannelName || '').toLowerCase(),
              description: state.editChannelDescription,
              color: state.editChannelColor,
              participants: state.editChannelParticipants,
            }
          );
        }
        dispatch({ type: 'CLOSE_EDIT_CHANNEL' });
      },
      handleExportProject: async (ch) => {
        try {
          const msgSnap = await getDocs(
            query(
              collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'messages'),
              where('channelId', '==', ch.id)
            )
          );
          const messages = msgSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          const noteSnap = await getDocs(
            query(
              collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'notes'),
              where('channelId', '==', ch.id)
            )
          );
          const note = !noteSnap.empty ? noteSnap.docs[0].data() : null;
          const blob = new Blob(
            [
              JSON.stringify(
                {
                  channel: ch,
                  tasks: (allTasks || []).filter((t) => t.channelId === ch.id),
                  messages,
                  note,
                },
                null,
                2
              ),
            ],
            { type: 'application/json' }
          );
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `export-${ch.name}.json`;
          link.click();
        } catch (err) {
          alert('Export failed: ' + err.message);
        }
      },
      handleShowProfile: (u) => dispatch({ type: 'SET_SELECTED_PROFILE', payload: u }),
      handleBackgroundUpload: async (file, channelId, type = 'audio', metadata = {}) => {
        const uploadId = metadata.uploadId || channelId || 'global';
        try {
          let exifData = null;
          let uploadFile = file;
          let originalFile = null;

          if (type === 'photo' && file.type !== 'image/gif') {
            try {
              const parsed = await exifr.parse(file);
              if (parsed) {
                exifData = {
                  latitude: parsed.latitude || null,
                  longitude: parsed.longitude || null,
                  DateTimeOriginal: (() => {
                    const dt = parsed.DateTimeOriginal || parsed.CreateDate || parsed.ModifyDate;
                    if (!dt) return null;
                    try { return new Date(dt).toISOString(); } catch { return null; }
                  })(),
                  Make: parsed.Make || null,
                  Model: parsed.Model || null,
                  LensModel: parsed.LensModel || null,
                  FNumber: parsed.FNumber || null,
                  ExposureTime: parsed.ExposureTime || null,
                  ISO: parsed.ISO || null,
                  FocalLength: parsed.FocalLength || null,
                };
                exifData = Object.fromEntries(Object.entries(exifData).filter(([_, v]) => v != null && v !== ''));
                if (Object.keys(exifData).length === 0) exifData = null;
              }
            } catch (e) {
              if (import.meta.env.DEV) console.log('Failed to parse EXIF:', e);
            }

            try {
              // Create a compressed thumbnail for fast viewing
              uploadFile = await compressImage(file, 1600, 1600, 0.85);
              originalFile = file;
            } catch (e) {
              console.error('Failed to compress image:', e);
            }
          }

          const uploadToB2 = async (targetFile, folderPrefix, onProgress) => {
            const fileName = `${folderPrefix}/${channelId || 'global'}/${Date.now()}_${targetFile.name}`;
            const response = await authedFetch('https://us-central1-project-project-30249.cloudfunctions.net/getBackblazeUploadUrl', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                fileName,
                contentType: targetFile.type,
                uid: authUser?.uid
              }),
            });

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Failed to get upload URL: ${errorText}`);
            }

            const responseData = await response.json();
            let { uploadUrl, authorizationToken, fileUrl } = responseData;
            
            if (fileUrl) {
              fileUrl = fileUrl.replace('f005.backblazeb2.com', 'f000.backblazeb2.com');
            }

            return new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open('POST', uploadUrl, true);
              xhr.setRequestHeader('Authorization', authorizationToken);
              xhr.setRequestHeader('X-Bz-File-Name', fileName.split('/').map(encodeURIComponent).join('/'));
              xhr.setRequestHeader('Content-Type', targetFile.type);
              xhr.setRequestHeader('X-Bz-Content-Sha1', 'do_not_verify');

              if (onProgress) {
                xhr.upload.onprogress = (e) => {
                  if (e.lengthComputable) onProgress(e.loaded, e.total);
                };
              }

              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                  resolve({ fileUrl, b2Data: JSON.parse(xhr.responseText), file: targetFile });
                } else {
                  reject(new Error(xhr.responseText));
                }
              };
              xhr.onerror = () => reject(new Error('Network error'));
              xhr.send(targetFile);
            });
          };

          const progressInfo = { thumbLoaded: 0, thumbTotal: 0, origLoaded: 0, origTotal: 0 };
          const updateCombinedProgress = () => {
            const loaded = progressInfo.thumbLoaded + progressInfo.origLoaded;
            const total = progressInfo.thumbTotal + progressInfo.origTotal;
            if (total > 0) {
              const pct = Math.round((loaded / total) * 100);
              dispatch({ type: 'UPDATE_UPLOAD_PROGRESS', payload: { channelId: uploadId, progress: pct } });
            }
          };

          const uploads = [];
          
          if (type === 'photo') {
             uploads.push(uploadToB2(uploadFile, 'photos', (loaded, total) => {
                 progressInfo.thumbLoaded = loaded;
                 progressInfo.thumbTotal = total;
                 updateCombinedProgress();
             }).then(res => ({ type: 'thumb', ...res })));
             
             if (originalFile) {
                 uploads.push(uploadToB2(originalFile, 'photos_original', (loaded, total) => {
                     progressInfo.origLoaded = loaded;
                     progressInfo.origTotal = total;
                     updateCombinedProgress();
                 }).then(res => ({ type: 'original', ...res })));
             }
          } else {
             const pathPrefix = type === 'audio' ? 'media' : 'notes';
             uploads.push(uploadToB2(uploadFile, pathPrefix, (loaded, total) => {
                 progressInfo.thumbLoaded = loaded;
                 progressInfo.thumbTotal = total;
                 updateCombinedProgress();
             }).then(res => ({ type: 'thumb', ...res })));
          }

          const results = await Promise.all(uploads);
          const thumbResult = results.find(r => r.type === 'thumb');
          const origResult = results.find(r => r.type === 'original');
          
          if (type === 'audio') {
            await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'media'), {
              title: uploadFile.name,
              url: thumbResult.fileUrl,
              type: 'audio',
              channelId: channelId || 'global',
              createdBy: authUser?.uid,
              createdAt: serverTimestamp(),
              b2FileId: thumbResult.b2Data.fileId,
              b2FileName: thumbResult.b2Data.fileName
            });
            if (metadata.onComplete) metadata.onComplete(thumbResult.fileUrl, thumbResult.b2Data);
          } else if (type === 'photo') {
            const photoDoc = {
              name: file.name, // Keep original file name for display
              url: thumbResult.fileUrl,
              type: 'photo',
              createdBy: authUser?.uid,
              createdAt: serverTimestamp(),
              b2FileId: thumbResult.b2Data.fileId,
              b2FileName: thumbResult.b2Data.fileName,
              size: uploadFile.size,
              contentType: uploadFile.type
            };
            if (origResult) {
              photoDoc.originalUrl = origResult.fileUrl;
              photoDoc.originalB2FileId = origResult.b2Data.fileId;
              photoDoc.originalSize = originalFile.size;
            }
            if (exifData) photoDoc.exif = exifData;
            if (exifData?.latitude && exifData?.longitude) {
              photoDoc.lat = exifData.latitude;
              photoDoc.lng = exifData.longitude;
            }
            await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'photos'), photoDoc);
            if (metadata.onComplete) metadata.onComplete(thumbResult.fileUrl, thumbResult.b2Data);
          } else if (metadata.onComplete) {
            metadata.onComplete(thumbResult.fileUrl, thumbResult.b2Data);
          }
          
          dispatch({ type: 'FINISH_UPLOAD', payload: uploadId });
        } catch (err) {
          console.error('B2 Upload Failed:', err);
          alert('Upload failed. Check console.');
          dispatch({ type: 'FINISH_UPLOAD', payload: uploadId });
        }
      },
      handleToggleReaction: async (m, emoji) => {
        const uid = authUser?.uid || 'test-uid-123';
        const r = m.reactions || {};
        const u = r[emoji] || [];
        const nu = u.includes(uid) ? u.filter((id) => id !== uid) : [...u, uid];
        await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'messages', m.id), {
          [`reactions.${emoji}`]: nu,
        });
        if (m.channelId) {
          try {
            await safeUpdateDoc(
              doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels', m.channelId),
              { lastActivity: serverTimestamp(), lastActorId: uid }
            );
          } catch (e) {
            /* ignore */
          }
        }
      },
      handleDeleteMessage: async (id) => {
        if (window.confirm('Delete?'))
          await safeDeleteDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'messages', id));
      },
      handleTogglePinMessage: async (msg, isPinned) => {
        const channelId = msg.channelId;
        const channel = (channels || []).find(c => c.id === channelId);
        if (!channel) return;
        
        if (isPinned) {
          if (channel.pinnedMessageId && channel.pinnedMessageId !== msg.id) {
            if (!window.confirm('Are you sure you want to replace the currently pinned message?')) {
              return;
            }
          }
          await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels', channelId), {
            pinnedMessageId: msg.id,
            pinnedMessageText: msg.text || ''
          });
        } else {
          await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels', channelId), {
            pinnedMessageId: null,
            pinnedMessageText: null
          });
        }
      },
      handleScrollToMessage: (id) => {
        document.getElementById(`message-${id}`)?.scrollIntoView({ behavior: 'smooth' });
      },
      handleStartDM: async (u) => {
        const targetUid = authUser?.uid || 'test-uid-123';
        if (u.id === targetUid) return;
        const existing = (channels || []).find(
          (c) =>
            c.type === 'dm' &&
            c.participants &&
            c.participants.includes(u.id) &&
            c.participants.includes(targetUid)
        );
        if (existing) {
          if (window.innerWidth >= 1024) {
            dispatch({ type: 'SET_OPEN_DM', payload: existing.id });
          } else {
            navigate('/home', { replace: true });
            navigate('/chat');
            navigate(`/p/${existing.id}/chat` + window.location.search);
          }
        } else {
          const res = await safeAddDoc(
            collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'channels'),
            {
              type: 'dm',
              participants: [targetUid, u.id],
              createdAt: serverTimestamp(),
              createdBy: targetUid,
              order: Date.now(),
            }
          );
          if (window.innerWidth >= 1024) {
            dispatch({ type: 'SET_OPEN_DM', payload: res.id });
          } else {
            navigate('/home', { replace: true });
            navigate('/chat');
            navigate(`/p/${res.id}/chat` + window.location.search);
          }
        }
      },
      projects: (channels || []).filter((c) => c.type !== 'dm' && c.id !== 'general_chat_global'),
      dms: (channels || []).filter((c) => c.type === 'dm'),
      participantNames: (allUsers || []).reduce((acc, u) => ({ ...acc, [u.id]: u.name }), {}),
      deletedChannels: deletedChannels,
      newTaskPriority: state.newTaskPriority,
      currentView: effectiveCurrentView,
      openDMChannelId: state.openDMChannelId,
      dmMessages: state.dmMessages,
      isMobile,
      isTouch,
      profile: authProfile,
      activeChannelId: effectiveActiveChannelId,
      location,
      appId: APP_ID_CONSTANT,
      handleLoadMore: () => dispatch({ type: 'LOAD_MORE_MESSAGES' }),
      messageLimit: state.messageLimit,
      isAddingEvent: state.isAddingEvent,
      editingEventId: state.editingEventId,
      newEventTitle: state.newEventTitle,
      newEventStartDate: state.newEventStartDate,
      newEventEndDate: state.newEventEndDate,
      newEventType: state.newEventType,
      visibleCalendars: state.visibleCalendars,
      calendarItems,
      getItemColor,
      calendarFontSize: state.calendarFontSize,
      calendarCellHeight: state.calendarCellHeight,
      collapsedHeaderIds: state.collapsedHeaderIds,
    }),
    [
      state,
      sensors,
      handleDragEnd,
      handleAddTask,
      handleAddPersonalTask,
      handleSendMessage,
      handleSaveEditMessage,
      toggleTask,
      toggleTaskStar,
      handleSaveEditTask,
      deleteTask,
      handleSaveEvent,
      handleDeleteEvent,
      taskInputRef,
      taskEditRef,
      chatInputRef,
      notepadRef,
      noteTimeoutRef,
      isLocalEditRef,
      personalTasks,
      tasks,
      allTasks,
      channels,
      allUsers,
      habits,
      habitLogs,
      authUser,
      authProfile,
      isVault,
      isBypass,
      navigate,
      authContext,
      APP_ID_CONSTANT,
      deferredPrompt,
      handleGoHome,
      handleGoTikiBar,
      effectiveActiveChannelId,
      effectiveCurrentView,
      state.chatChannelId,
      state.activeChannelId,
      state.editingTaskId,
      state.editTaskText,
      state.editTaskDueDate,
      state.newTask,
      state.newTaskPriority,
      state.newTaskDate,
      state.newTaskDueDate,
      state.newTaskIsHeader,
      state.selectedHeaderId,
      state.taskFontSize,
      state.calendarFontSize,
      state.calendarCellHeight,
      state.collapsedHeaderIds,
      state.hiddenCompletedHeaderIds,
      state.showCompletedTasks,
      state.showOnlyStarredTasks,
      state.isSavingNote,
      state.isSpellCheckEnabled,
      state.editingChannelId,
      state.editChannelName,
      state.editChannelDescription,
      state.editChannelColor,
      state.editChannelParticipants,
      state.isCreatingChannel,
      state.newChannelName,
      state.newChannelColor,
      state.createError,
      state.isAddingEvent,
      state.editingEventId,
      state.newEventTitle,
      state.newEventStartDate,
      state.newEventEndDate,
      state.newEventType,
      state.editTaskPriority,
      state.visibleCalendars,
      state.mapStartDate,
      state.mapEndDate,
      state.searchRadius,
      state.customCenter,
      state.friendFilter,
      state.selectedTripId,
      authProfile?.motivationalMessage,
      authProfile?.avatar,
      authProfile?.cities,
      isMobile,
      isTouch,
      events,
      linkify,
      saveUserProfile,
      calendarItems,
      location,
    ]
  );

  useEffect(() => {
    if (isVault) {
      window.testState = {
        ...state,
        user: effectiveUser,
        channels,
        allTasks,
        allUsers,
        personalTasks,
        username: layoutProps.username,
        bio: layoutProps.bio,
        motivationalMessage: layoutProps.motivationalMessage,
        userAvatar: layoutProps.userAvatar,
      };
      window.dispatch = dispatch;
      window.testFirestore = {
        addDoc,
        setDoc,
        getDoc,
        onSnapshot,
        collection,
        serverTimestamp,
        getDocs,
        query,
        where,
        doc,
        updateDoc,
        deleteDoc,
      };
      window.testDb = db;
      window.testAppId = APP_ID_CONSTANT;
      window.testHandleDragEnd = handleDragEnd;
      window.saveUserProfile = layoutProps.saveUserProfile;
      window.testDispatch = dispatch;
      window.handleCreateChannel = layoutProps.handleCreateChannel;
      window.handleAddTask = handleAddTask;
      window.manualCreateTask = async (text, isHeader = false) => {
        const channelId = state.activeChannelId;
        const channelTasks = (allTasks || []).filter((t) => t.channelId === channelId);
        const order =
          Math.ceil(channelTasks.reduce((max, t) => Math.max(max, t.order || 0), 0)) + 1;
        await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'tasks'), {
          text,
          channelId,
          parentId: null,
          completed: false,
          isHeader: !!isHeader,
          createdAt: serverTimestamp(),
          order,
          priority: 'High',
        });
      };
      window.initGlobalChannel = async () => {
        await safeSetDoc(
          doc(
            db,
            'artifacts',
            APP_ID_CONSTANT,
            'public',
            'data',
            'channels',
            'general_chat_global'
          ),
          {
            name: 'tiki bar',
            type: 'public',
            createdAt: serverTimestamp(),
            order: 0,
          },
          { merge: true }
        );
      };
    }
  }, [
    state,
    channels,
    allTasks,
    allUsers,
    isVault,
    handleDragEnd,
    layoutProps.saveUserProfile,
    layoutProps.handleCreateChannel,
  ]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (state.isAddingEvent) dispatch({ type: 'CLOSE_ADD_EVENT' });
        if (state.isDayDetailOpen) dispatch({ type: 'CLOSE_DAY_DETAIL' });
        if (state.editingChannelId) dispatch({ type: 'CLOSE_EDIT_CHANNEL' });
        if (state.isCreatingChannel) dispatch({ type: 'CLOSE_CREATE_CHANNEL' });
        if (state.selectedProfileUser) dispatch({ type: 'SET_SELECTED_PROFILE', payload: null });
        if (state.isURLTreeOpen) dispatch({ type: 'TOGGLE_URL_TREE' });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    state.isAddingEvent,
    state.editingChannelId,
    state.isCreatingChannel,
    state.selectedProfileUser,
    state.isURLTreeOpen,
  ]);

  useEffect(() => {
    const path = location?.pathname;
    const search = location?.search || '';

    if (path.startsWith('/p/')) {
      const parts = path.split('/');
      const channelId = parts[2];

      if (channelId) {
        if (channelId === 'general_chat_global') return;

        if (channels && channels.length > 0 && !dataLoading) {
          const channel = channels.find((c) => c.id === channelId);
          const uid = authUser?.uid || 'test-uid-123';

          // Security Guard: Personal IDs must match the current user
          if (channelId.startsWith('personal-')) {
            const isMyPersonal =
              channelId === `personal-${uid}` || channelId === `personal-note-${uid}`;
            if (!isMyPersonal) {
              navigate('/home' + search);
              return;
            }
          }

          if (channel && channel.type === 'dm' && window.innerWidth >= 1024 && parts.length === 3) {
            dispatch({ type: 'SET_OPEN_DM', payload: channelId });
            navigate('/home' + search);
            return;
          }

          if (channel && channel.type !== 'dm' && !channelId.startsWith('personal-')) {
            const participants = channel.participants || [channel.createdBy];
            if (!participants.includes(uid)) {
              navigate('/home' + search);
              return;
            }
          } else if (!channel && !channelId.startsWith('personal-')) {
            // Only redirect if still missing after a short delay to allow for Firestore sync
            const timeoutId = setTimeout(() => {
              const stillMissing = !channels.find((c) => c.id === channelId);
              if (stillMissing && location?.pathname === path) {
                navigate('/home' + search);
              }
            }, 1500);
            return () => clearTimeout(timeoutId);
          }
        }

        const pathItems = getProjectPath(channelId, channels || []);
        if (pathItems.length > 0) {
          const pathIds = [];
          pathItems.forEach((p) => {
            pathIds.push(p.id);
            pathIds.push(`starred:${p.id}`);
          });
          const needsExpand = pathIds.some((id) => !state.expandedIds[id]);
          if (needsExpand) {
            dispatch({ type: 'EXPAND_PATH', payload: pathIds });
          }
        }
      }
    }
  }, [
    location?.pathname,
    channels,
    authUser,
    navigate,
    location?.search,
    dataLoading,
    state.expandedIds,
  ]);

  if (!effectiveUser && !isVault)
    return <LoginScreen onSignIn={handleSignIn} onTestSignIn={handleTestSignIn} />;

  return (
    <>
    <audio ref={radioAudioRef} src={state.radioStation?.streamUrl} preload="none" />
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={layoutProps.handleDragStart}
      onDragEnd={layoutProps.handleDragEnd}
    >
      <div className="h-full relative overflow-hidden">
        <Routes>
          <Route
            path="/home"
            element={
              isMobile ? (
                <MobileHome {...layoutProps} user={effectiveUser} allUsers={allUsers} />
              ) : (
                <DesktopLayout key="home" {...layoutProps} user={effectiveUser} />
              )
            }
          />
          <Route
            path="/p/:id"
            element={
              isMobile ? (
                <MobileProjectView {...layoutProps} user={effectiveUser} />
              ) : (
                <DesktopLayout key={`project-${effectiveActiveChannelId}`} {...layoutProps} user={effectiveUser} />
              )
            }
          />
          <Route
            path="/p/:id/chat"
            element={
              isMobile ? (
                <MobileProjectView {...layoutProps} activeModule="chat" user={effectiveUser} />
              ) : (
                <SoloWrapper
                  hideHeader={true}
                  title="Chat"
                  path={getProjectPath(effectiveActiveChannelId, channels)}
                  onNavigate={navigate}
                  fallbackName={
                    effectiveActiveChannelId === 'general_chat_global'
                      ? 'Tiki Bar'
                      : (channels || []).find((c) => c.id === effectiveActiveChannelId)?.name
                  }
                  fallbackId={effectiveActiveChannelId}
                  headerClocks={headerClocks}
                >
                  {' '}
                  <ChatView
                    {...layoutProps}
                    messages={layoutProps.messages}
                    isFullWidth={true}
                    isChatOpen={true}
                    path={getProjectPath(effectiveActiveChannelId, channels)}
                  />{' '}
                </SoloWrapper>
              )
            }
          />
          <Route
            path="/p/:id/tasks"
            element={
              isMobile ? (
                <MobileProjectView {...layoutProps} activeModule="tasks" user={effectiveUser} />
              ) : (
                <SoloWrapper
                  hideHeader={true}
                  title="Tasks"
                  path={getProjectPath(effectiveActiveChannelId, channels)}
                  onNavigate={navigate}
                  fallbackName={
                    effectiveActiveChannelId === 'general_chat_global'
                      ? 'Tiki Bar'
                      : (channels || []).find((c) => c.id === effectiveActiveChannelId)?.name
                  }
                  fallbackId={effectiveActiveChannelId}
                  headerClocks={headerClocks}
                >
                  {' '}
                  <TasksView {...layoutProps} path={getProjectPath(effectiveActiveChannelId, channels)} />{' '}
                </SoloWrapper>
              )
            }
          />
          <Route
            path="/p/:id/notes"
            element={
              isMobile ? (
                <MobileProjectView {...layoutProps} activeModule="notes" user={effectiveUser} />
              ) : (
                <SoloWrapper
                  hideHeader={true}
                  title="Notepad"
                  path={getProjectPath(effectiveActiveChannelId, channels)}
                  onNavigate={navigate}
                  fallbackName={
                    effectiveActiveChannelId === 'general_chat_global'
                      ? 'Tiki Bar'
                      : (channels || []).find((c) => c.id === effectiveActiveChannelId)?.name
                  }
                  fallbackId={effectiveActiveChannelId}
                  headerClocks={headerClocks}
                >
                  {' '}
                  <NotepadView
                    {...layoutProps}
                    user={effectiveUser}
                    username={layoutProps.username}
                    currentView="workspace"
                    activeChannelId={effectiveActiveChannelId}
                    activeColorHex={activeColorHex}
                    isSavingNote={state.isSavingNote}
                    isSpellCheckEnabled={state.isSpellCheckEnabled}
                    dispatch={dispatch}
                    noteTimeoutRef={noteTimeoutRef}
                    isLocalEditRef={isLocalEditRef}
                    handleSaveNote={layoutProps.handleSaveNote}
                    notepadRef={notepadRef}
                    path={getProjectPath(effectiveActiveChannelId, channels)}
                    activeUploads={state.activeUploads}
                    handleBackgroundUpload={layoutProps.handleBackgroundUpload}
                  />{' '}
                </SoloWrapper>
              )
            }
          />
          <Route
            path="/chat"
            element={
              isMobile ? (
                <MobileChatList {...layoutProps} user={effectiveUser} />
              ) : (
                <DesktopLayout key="chat" {...layoutProps} user={effectiveUser} />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isMobile ? (
                <MobileSettings {...layoutProps} user={effectiveUser} profile={authProfile} />
              ) : (
                <DesktopLayout key="settings" {...layoutProps} user={effectiveUser} />
              )
            }
          />
          <Route
            path="/calendar"
            element={
              isMobile ? (
                <MobileCalendarView
                  tasks={tasks}
                  personalTasks={personalTasks}
                  events={events}
                  calendarItems={calendarItems}
                  visibleCalendars={state.visibleCalendars}
                  dispatch={dispatch}
                  navigate={navigate}
                  profile={authProfile}
                  authUser={authUser}
                  habits={habits}
                  habitLogs={habitLogs}
                  handleLogHabit={layoutProps.handleLogHabit}
                  toggleTask={toggleTask}
                  getItemColor={getItemColor}
                  searchRadius={state.searchRadius}
                />
              ) : (
                <DesktopLayout {...layoutProps} user={effectiveUser} />
              )
            }
          />
          <Route
            path="/film-tv"
            element={
              isMobile ? (
                <div className="h-full bg-white p-10">
                  <h1 className="text-3xl font-black text-zinc-900 tracking-tighter uppercase mb-4">
                    Film/TV
                  </h1>
                  <p className="text-zinc-500">Film & TV tracker coming soon...</p>
                  <button
                    onClick={() => navigate(-1)}
                    className="mt-8 px-6 py-2 bg-zinc-900 text-white rounded-xl font-bold"
                  >
                    Go Back
                  </button>
                </div>
              ) : (
                <DesktopLayout {...layoutProps} user={effectiveUser} />
              )
            }
          />
          <Route
            path="/media"
            element={
              isMobile ? (
                <div className="h-full bg-white p-10">
                  <h1 className="text-3xl font-black text-zinc-900 tracking-tighter uppercase mb-4">
                    Photos
                  </h1>
                  <p className="text-zinc-500">Photo library coming soon...</p>
                  <button
                    onClick={() => navigate(-1)}
                    className="mt-8 px-6 py-2 bg-zinc-900 text-white rounded-xl font-bold"
                  >
                    Go Back
                  </button>
                </div>
              ) : (
                <DesktopLayout {...layoutProps} user={effectiveUser} />
              )
            }
          />
          <Route
            path="/social"
            element={
              <SocialFeedView 
                photos={state.photos} 
                user={effectiveUser} 
                allUsers={state.allUsers || []} 
                navigate={navigate} 
                b2Token={b2Token}
                isMobile={isMobile}
              />
            }
          />
          {/* /weather disabled */}
          <Route
            path="/watch"
            element={
              isMobile ? (
                <div className="h-full bg-white p-10">
                  <h1 className="text-3xl font-black text-zinc-900 tracking-tighter uppercase mb-4">
                    Watch
                  </h1>
                  <p className="text-zinc-500">Watch/Listen dashboard coming soon...</p>
                  <button
                    onClick={() => navigate(-1)}
                    className="mt-8 px-6 py-2 bg-zinc-900 text-white rounded-xl font-bold"
                  >
                    Go Back
                  </button>
                </div>
              ) : (
                <DesktopLayout {...layoutProps} user={effectiveUser} currentView="watch" />
              )
            }
          />
          <Route
            path="/rss"
            element={
              isMobile ? (
                <div className="h-full bg-white p-10">
                  <h1 className="text-3xl font-black text-zinc-900 tracking-tighter uppercase mb-4">
                    RSS Feeds
                  </h1>
                  <p className="text-zinc-500">RSS viewer coming soon...</p>
                  <button
                    onClick={() => navigate(-1)}
                    className="mt-8 px-6 py-2 bg-zinc-900 text-white rounded-xl font-bold"
                  >
                    Go Back
                  </button>
                </div>
              ) : (
                <DesktopLayout key="rss" {...layoutProps} user={effectiveUser} currentView="rss" />
              )
            }
          />
          <Route
            path="/database"
            element={
              isMobile ? (
                <DatabaseView allUsers={allUsers} allTasks={allTasks} habits={habits} mapStartDate={state.mapStartDate} mapEndDate={state.mapEndDate} calendarItems={state.calendarItems} visibleCalendars={state.visibleCalendars} dispatch={dispatch} />
              ) : (
                <DesktopLayout 
                  key="database"
                  {...layoutProps} 
                  user={effectiveUser} 
                  currentView="database"
                  customView={<DatabaseView allUsers={allUsers} allTasks={allTasks} habits={habits} mapStartDate={state.mapStartDate} mapEndDate={state.mapEndDate} calendarItems={state.calendarItems} visibleCalendars={state.visibleCalendars} dispatch={dispatch} />} 
                />
              )
            }
          />
          <Route
            path="/database/tags"
            element={
              isMobile ? (
                <TagManagerPage />
              ) : (
                <DesktopLayout 
                  key="database-tags"
                  {...layoutProps} 
                  user={effectiveUser} 
                  currentView="database"
                  customView={<TagManagerPage />} 
                />
              )
            }
          />
          <Route
            path="/eventchanges"
            element={
              isMobile ? (
                <EventChangesView />
              ) : (
                <DesktopLayout 
                  key="eventchanges"
                  {...layoutProps} 
                  user={effectiveUser} 
                  currentView="eventchanges"
                  customView={<EventChangesView />} 
                />
              )
            }
          />
          <Route
            path="*"
            element={
              isMobile ? (
                <MobileHome {...layoutProps} user={effectiveUser} />
              ) : (
                <DesktopLayout {...layoutProps} user={effectiveUser} />
              )
            }
          />{' '}
        </Routes>
      </div>

      {state.isCreatingChannel && (
        <DraggableModal
          dataTestId="create-project-modal"
          onClose={() => dispatch({ type: 'CLOSE_CREATE_CHANNEL' })}
        >
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-black text-zinc-900 tracking-tighter">New Project</h2>
            <p className="text-xs text-zinc-400 font-medium">Create a fresh workspace</p>
          </div>
          <input
            autoFocus
            type="text"
            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-2 py-1.5 text-lg font-bold outline-none focus:border-zinc-900"
            placeholder="Project name..."
            value={state.newChannelName}
            onChange={(e) =>
              dispatch({
                type: 'SET_NEW_CHANNEL_INPUT',
                payload: { newChannelName: e.target.value },
              })
            }
            onKeyDown={(e) => e.key === 'Enter' && layoutProps.handleCreateChannel()}
          />
          <div className="grid grid-cols-7 gap-2">
            {MUTED_COLORS.map((c) => (
              <button
                key={c.id}
                onClick={() =>
                  dispatch({ type: 'SET_NEW_CHANNEL_INPUT', payload: { newChannelColor: c.id } })
                }
                className={`w-8 h-8 rounded-full border-2 ${state.newChannelColor === c.id ? 'border-zinc-900 scale-110 shadow-sm' : 'border-transparent'}`}
                style={{ backgroundColor: c.hex }}
              />
            ))}
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => dispatch({ type: 'CLOSE_CREATE_CHANNEL' })}
              className="flex-1 py-3 bg-zinc-100 text-zinc-500 font-black uppercase tracking-widest text-[10px] rounded-xl active:bg-zinc-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={layoutProps.handleCreateChannel}
              className="flex-1 py-3 bg-zinc-900 text-white font-black uppercase tracking-widest text-[10px] rounded-xl active:bg-black transition-colors shadow-lg"
            >
              Create
            </button>
          </div>
        </DraggableModal>
      )}

      {state.isAddingEvent && (
        <DraggableModal
          dataTestId="add-event-modal"
          onClose={() => dispatch({ type: 'CLOSE_ADD_EVENT' })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              layoutProps.handleSaveEvent();
            }
          }}
        >
          {(() => {
            const isProjectTask = state.editingEventChannelId && !state.editingEventChannelId.startsWith('personal-');
            
            const handleNaturalLanguageParse = async () => {
              const chrono = await import('chrono-node');
              const titleStr = state.newEventType === 'birthday' ? state.newEventPersonName : state.newEventTitle;
              if (!titleStr) return;
              
              const refDateStr = state.newEventStartDate || state.mapStartDate || new Date().toISOString().split('T')[0];
              const refDate = new Date(refDateStr + 'T12:00:00');
              
              const parsed = chrono.casual.parse(titleStr, refDate);
              if (parsed && parsed.length > 0) {
                const result = parsed[0];
                const start = result.start;
                const end = result.end;
                
                const currentHour = new Date().getHours();
                if (start && start.isCertain('hour') && !start.isCertain('meridiem')) {
                  const h = start.get('hour');
                  // If hour is less than 12 and less than current hour, assume PM
                  // e.g. at 2pm, "5" -> 5 < 14 -> 17 (5pm)
                  if (h < 12 && h < currentHour) {
                    start.assign('meridiem', 1);
                    start.assign('hour', h + 12);
                  }
                }
                if (end && end.isCertain('hour') && !end.isCertain('meridiem')) {
                  const h = end.get('hour');
                  const compareHour = start && start.isCertain('hour') ? start.get('hour') : currentHour;
                  // If end hour is less than start hour (or current hour), assume PM
                  if (h < 12 && h < compareHour) {
                    end.assign('meridiem', 1);
                    end.assign('hour', h + 12);
                  }
                }
                
                const updates = {};
                
                if (start) {
                  const sDate = start.date();
                  const yyyy = sDate.getFullYear();
                  const mm = String(sDate.getMonth() + 1).padStart(2, '0');
                  const dd = String(sDate.getDate()).padStart(2, '0');
                  updates.newEventStartDate = `${yyyy}-${mm}-${dd}`;
                  
                  if (start.isCertain('hour')) {
                    updates.newEventStartTime = sDate.toTimeString().substring(0, 5);
                    updates.newEventIsAllDay = false;
                  }
                }
                
                if (end) {
                  const eDate = end.date();
                  const yyyy = eDate.getFullYear();
                  const mm = String(eDate.getMonth() + 1).padStart(2, '0');
                  const dd = String(eDate.getDate()).padStart(2, '0');
                  updates.newEventEndDate = `${yyyy}-${mm}-${dd}`;
                  
                  if (end.isCertain('hour')) {
                    updates.newEventEndTime = eDate.toTimeString().substring(0, 5);
                  }
                } else if (start) {
                  updates.newEventEndDate = updates.newEventStartDate;
                  if (start.isCertain('hour')) {
                    const sDate = start.date();
                    // Default to 1 hour later
                    const defaultEndDate = new Date(sDate.getTime() + 60 * 60 * 1000);
                    const yyyy = defaultEndDate.getFullYear();
                    const mm = String(defaultEndDate.getMonth() + 1).padStart(2, '0');
                    const dd = String(defaultEndDate.getDate()).padStart(2, '0');
                    updates.newEventEndDate = `${yyyy}-${mm}-${dd}`;
                    updates.newEventEndTime = defaultEndDate.toTimeString().substring(0, 5);
                  }
                }
                
                let newTitle = titleStr.replace(result.text, '');
                // Clean up trailing or leading prepositions like "at", "on", "@"
                newTitle = newTitle.replace(/(?:^|\s)(at|on|@)\s*$/i, '');
                newTitle = newTitle.replace(/^\s*(at|on|@)(?:\s|$)/i, '');
                newTitle = newTitle.replace(/\s+/g, ' ').trim();
                newTitle = newTitle.replace(/\b\w/g, c => c.toUpperCase());
                if (!newTitle) newTitle = 'New Event';
                
                if (state.newEventType === 'birthday') {
                  updates.newEventPersonName = newTitle;
                } else {
                  updates.newEventTitle = newTitle;
                }
                
                dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: updates });
              }
            };

            return (
              <>
                <div className="flex flex-col gap-1">
                  <h2 className="text-xl font-black text-zinc-900 tracking-tighter">
                    {isProjectTask 
                      ? 'Edit Project Task'
                      : state.newEventType === 'google-event'
                        ? state.newEventCalendarName || 'Google Calendar Event'
                        : state.editingEventId
                          ? 'Edit Event / Task / Trip'
                          : 'New Event / Task / Trip'}
                  </h2>
                </div>

            <div className="flex flex-col gap-1 relative group">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest">
                    {state.newEventType === 'trip' ? 'Event (Optional)' : state.newEventType === 'birthday' ? "Person's Name" : 'Title'}
                  </label>
                  <button
                    type="button"
                    onClick={handleNaturalLanguageParse}
                    className="flex items-center gap-1 text-[10px] text-blue-500 font-bold opacity-50 hover:opacity-100 transition-opacity"
                    title="Use Shift+Enter for natural language"
                  >
                    <Sparkles size={12} />
                  </button>
                </div>
                {(state.newEventType === 'event' || state.newEventType === 'tentative' || state.newEventType === 'concert' || state.newEventType === 'restaurant' || state.newEventType === 'sight' || state.newEventType === 'hotel' || state.newEventType === 'holiday' || state.newEventType === 'task') && (
                  <button
                    type="button"
                    onClick={() => dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: { newEventIsStarred: !state.newEventIsStarred } })}
                    className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-widest transition-colors ${state.newEventIsStarred ? 'text-amber-500' : 'text-zinc-300 hover:text-zinc-400'}`}
                  >
                    <Star size={12} fill={state.newEventIsStarred ? 'currentColor' : 'none'} />
                    {state.newEventIsStarred ? 'Starred' : 'Star Event'}
                  </button>
                )}
              </div>
              <input
                autoFocus
                disabled={state.newEventType === 'google-event'}
                type="text"
                className={`w-full bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-lg font-bold outline-none focus:border-zinc-900 ${state.newEventType === 'google-event' ? 'opacity-70' : ''}`}
                placeholder={
                  state.newEventType === 'trip' ? 'e.g. Conference, Vacation...' : state.newEventType === 'birthday' ? "e.g. Brett Gaddy" : 'Notes or details...'
                }
                value={state.newEventType === 'birthday' ? state.newEventPersonName : state.newEventTitle}
                onChange={(e) =>
                  dispatch({
                    type: 'SET_NEW_EVENT_INPUT',
                    payload: state.newEventType === 'birthday' ? { newEventPersonName: e.target.value } : { newEventTitle: e.target.value },
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNaturalLanguageParse();
                  }
                }}
              />
            </div>

            {state.newEventType === 'birthday' && (
              <div className="flex flex-col gap-1">
                <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                  Birth Year
                </label>
                <input
                  type="number"
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-sm font-bold outline-none focus:border-zinc-900 tabular-nums"
                  placeholder="e.g. 1990"
                  value={state.newEventBirthYear}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_NEW_EVENT_INPUT',
                      payload: { newEventBirthYear: e.target.value },
                    })
                  }
                />
              </div>
            )}

            {state.editingEventId === 'my-birthday' && (
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                    Bio
                  </label>
                  <textarea
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-sm font-bold outline-none focus:border-zinc-900 min-h-[60px] resize-none"
                    placeholder="Tell us about yourself..."
                    value={state.newEventBio}
                    onChange={(e) =>
                      dispatch({
                        type: 'SET_NEW_EVENT_INPUT',
                        payload: { newEventBio: e.target.value },
                      })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                    Motivational Message
                  </label>
                  <input
                    type="text"
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-sm font-bold outline-none focus:border-zinc-900"
                    placeholder="e.g. Stay positive!"
                    value={state.newEventMotivation}
                    onChange={(e) =>
                      dispatch({
                        type: 'SET_NEW_EVENT_INPUT',
                        payload: { newEventMotivation: e.target.value },
                      })
                    }
                  />
                </div>
              </>
            )}

            {(state.newEventType === 'birthday' || state.newEventType === 'holiday') ? (
              <div className="flex flex-col gap-1">
                <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                  {state.newEventType === 'holiday' ? 'Holiday Date' : 'Birthday'}
                </label>
                <div className="flex gap-2">
                  <select
                    className="flex-1 bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-sm font-bold outline-none focus:border-zinc-900"
                    value={state.newEventStartDate ? state.newEventStartDate.split('-')[1] : ''}
                    onChange={(e) => {
                      const month = e.target.value;
                      const day = state.newEventStartDate ? state.newEventStartDate.split('-')[2] : '01';
                      const val = `2000-${month}-${day}`;
                      dispatch({
                        type: 'SET_NEW_EVENT_INPUT',
                        payload: { newEventStartDate: val, newEventEndDate: val },
                      });
                    }}
                  >
                    <option value="">Month</option>
                    {Array.from({ length: 12 }, (_, i) => {
                      const m = String(i + 1).padStart(2, '0');
                      return <option key={m} value={m}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</option>;
                    })}
                  </select>
                  <select
                    className="w-32 bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-sm font-bold outline-none focus:border-zinc-900"
                    value={state.newEventStartDate ? state.newEventStartDate.split('-')[2] : ''}
                    onChange={(e) => {
                      const day = e.target.value;
                      const month = state.newEventStartDate ? state.newEventStartDate.split('-')[1] : '01';
                      const val = `2000-${month}-${day}`;
                      dispatch({
                        type: 'SET_NEW_EVENT_INPUT',
                        payload: { newEventStartDate: val, newEventEndDate: val },
                      });
                    }}
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => {
                      const d = String(i + 1).padStart(2, '0');
                      return <option key={d} value={d}>{d}</option>;
                    })}
                  </select>
                </div>
              </div>
            ) : state.newEventType === 'task' ? (
              <div className="flex flex-col gap-1">
                <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                  Due Date
                </label>
                <input
                  type="date"
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-sm font-bold outline-none focus:border-zinc-900"
                  value={state.newEventStartDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    dispatch({
                      type: 'SET_NEW_EVENT_INPUT',
                      payload: { newEventStartDate: val, newEventEndDate: val },
                    });
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-3 mb-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                      Start Date
                    </label>
                    <input
                      disabled={state.newEventType === 'google-event'}
                      type="date"
                      className={`w-full bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-sm font-bold outline-none focus:border-zinc-900 ${state.newEventType === 'google-event' ? 'opacity-70' : ''}`}
                      value={state.newEventStartDate}
                      onChange={(e) => {
                        const newStart = e.target.value;
                        const updates = { newEventStartDate: newStart };
                        if (state.newEventEndDate && state.newEventEndDate < newStart) {
                          updates.newEventEndDate = newStart;
                        }
                        dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: updates });
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                      End Date
                    </label>
                    <input
                      disabled={state.newEventType === 'google-event'}
                      type="date"
                      className={`w-full bg-zinc-50 border rounded-none px-2 py-1.5 text-sm font-bold outline-none focus:border-zinc-900 ${state.newEventEndDate && state.newEventEndDate < state.newEventStartDate ? 'border-red-500 text-red-500 bg-red-50/50' : 'border-zinc-200'} ${state.newEventType === 'google-event' ? 'opacity-70' : ''}`}
                      value={state.newEventEndDate}
                      placeholder={state.newEventType === 'habit' ? 'Forever' : ''}
                      onChange={(e) => {
                        dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: { newEventEndDate: e.target.value } });
                      }}
                    />
                  </div>
                </div>

                {state.newEventType !== 'habit' && !state.newEventIsAllDay && (
                  <div className="grid grid-cols-2 gap-3 animate-in slide-in-from-top-2 duration-200">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                        Start Time
                      </label>
                      <SmartTimeInput
                        disabled={state.newEventType === 'google-event'}
                        className={`w-full bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-sm font-bold outline-none focus:border-zinc-900 ${state.newEventType === 'google-event' ? 'opacity-70' : ''}`}
                        value={state.newEventStartTime}
                        onChange={(newStart) => {
                          const updates = { newEventStartTime: newStart };
                          if (state.newEventStartDate === state.newEventEndDate) {
                            if (state.newEventEndTime <= newStart) {
                              const [h, m] = newStart.split(':').map(Number);
                              updates.newEventEndTime = `${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                            }
                          }
                          dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: updates });
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                        End Time
                      </label>
                      <SmartTimeInput
                        disabled={state.newEventType === 'google-event'}
                        className={`w-full bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-sm font-bold outline-none focus:border-zinc-900 ${state.newEventType === 'google-event' ? 'opacity-70' : ''}`}
                        value={state.newEventEndTime}
                        onChange={(newEnd) => {
                          const updates = { newEventEndTime: newEnd };
                          if (state.newEventStartDate === state.newEventEndDate) {
                            if (newEnd <= state.newEventStartTime) {
                              const [h, m] = newEnd.split(':').map(Number);
                              updates.newEventStartTime = `${String((h + 23) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                            }
                          }
                          dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: updates });
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {state.newEventType === 'trip' && (
              <div className="grid grid-cols-2 gap-3 -mt-1">
                {/* Flight In Section */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between px-1">
                    <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest">
                      Flight In
                    </label>
                    <button
                      onClick={() =>
                        dispatch({
                          type: 'SET_NEW_EVENT_INPUT',
                          payload: { newTripFlightIn: [...(state.newTripFlightIn || []), ''] },
                        })
                      }
                      className="text-[10px] font-black uppercase text-blue-500 hover:text-blue-700 tracking-tighter"
                    >
                      + Add
                    </button>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {(state.newTripFlightIn || []).map((flight, idx) => (
                      <div key={`flight-in-${idx}`} className="relative flex items-center group/leg">
                        <input
                          type="text"
                          className={`w-full bg-zinc-50 border border-zinc-200 rounded-none py-2 text-sm font-bold outline-none focus:border-zinc-900 uppercase placeholder:normal-case ${(state.newTripFlightIn || []).length > 1 ? 'pl-4 pr-10' : 'px-4'}`}
                          placeholder={idx === 0 ? "e.g. AA123" : `Leg ${idx + 1}`}
                          value={flight}
                          onChange={(e) => {
                            const next = [...state.newTripFlightIn];
                            next[idx] = e.target.value.toUpperCase();
                            dispatch({
                              type: 'SET_NEW_EVENT_INPUT',
                              payload: { newTripFlightIn: next },
                            });
                          }}
                        />
                        {(state.newTripFlightIn || []).length > 1 && (
                          <button
                            onClick={() => {
                              const next = (state.newTripFlightIn || []).filter((_, i) => i !== idx);
                              dispatch({
                                type: 'SET_NEW_EVENT_INPUT',
                                payload: { newTripFlightIn: next },
                              });
                            }}
                            className="absolute right-2 text-zinc-400 hover:text-red-500 transition-colors p-1"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Flight Out Section */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between px-1">
                    <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest">
                      Flight Out
                    </label>
                    <button
                      onClick={() =>
                        dispatch({
                          type: 'SET_NEW_EVENT_INPUT',
                          payload: { newTripFlightOut: [...(state.newTripFlightOut || []), ''] },
                        })
                      }
                      className="text-[10px] font-black uppercase text-blue-500 hover:text-blue-700 tracking-tighter"
                    >
                      + Add
                    </button>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {(state.newTripFlightOut || []).map((flight, idx) => (
                      <div key={`flight-out-${idx}`} className="relative flex items-center group/leg">
                        <input
                          type="text"
                          className={`w-full bg-zinc-50 border border-zinc-200 rounded-none py-2 text-sm font-bold outline-none focus:border-zinc-900 uppercase placeholder:normal-case ${(state.newTripFlightOut || []).length > 1 ? 'pl-4 pr-10' : 'px-4'}`}
                          placeholder={idx === 0 ? "e.g. DL456" : `Leg ${idx + 1}`}
                          value={flight}
                          onChange={(e) => {
                            const next = [...state.newTripFlightOut];
                            next[idx] = e.target.value.toUpperCase();
                            dispatch({
                              type: 'SET_NEW_EVENT_INPUT',
                              payload: { newTripFlightOut: next },
                            });
                          }}
                        />
                        {(state.newTripFlightOut || []).length > 1 && (
                          <button
                            onClick={() => {
                              const next = (state.newTripFlightOut || []).filter((_, i) => i !== idx);
                              dispatch({
                                type: 'SET_NEW_EVENT_INPUT',
                                payload: { newTripFlightOut: next },
                              });
                            }}
                            className="absolute right-2 text-zinc-400 hover:text-red-500 transition-colors p-1"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {state.newEventType === 'trip' && (
              <div className="flex flex-col gap-1 mt-2">
                <div className="flex items-center justify-between px-1">
                  <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest">
                    Hotels
                  </label>
                  <button
                    onClick={() =>
                      dispatch({
                        type: 'SET_NEW_EVENT_INPUT',
                        payload: {
                          newTripHotels: [
                            ...(state.newTripHotels || []),
                            {
                              name: '',
                              address: '',
                              lat: null,
                              lng: null,
                              startDate: state.newEventStartDate,
                              endDate: state.newEventEndDate,
                              checkInTime: '',
                              checkOutTime: '',
                              notes: '',
                            },
                          ],
                        },
                      })
                    }
                    className="text-[10px] font-black uppercase text-blue-500 hover:text-blue-700 tracking-tighter"
                  >
                    + Add
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  {(state.newTripHotels || []).map((hotel, idx) => (
                    <div key={`hotel-${idx}`} className="bg-zinc-50 p-2 flex flex-col gap-2 relative border border-zinc-200">
                      <button
                        onClick={() => {
                          if (!window.confirm('Are you sure you want to remove this hotel?')) return;
                          const next = (state.newTripHotels || []).filter((_, i) => i !== idx);
                          dispatch({
                            type: 'SET_NEW_EVENT_INPUT',
                            payload: { newTripHotels: next },
                          });
                        }}
                        className="absolute right-1 top-1 text-zinc-400 hover:text-red-500 transition-colors p-1 z-10"
                      >
                        <X size={12} />
                      </button>
                      <div className="flex flex-col gap-1">
                        <label className="text-[8px] font-black uppercase text-zinc-400 tracking-widest px-0.5">Hotel Search</label>
                        <CitySearch
                          types={['lodging']}
                          minimal={true}
                          city={{ name: hotel.name }}
                          onCityChange={(_, result) => {
                            const next = [...state.newTripHotels];
                            next[idx] = {
                              ...next[idx],
                              name: result.venueName || result.name,
                              address: result.name,
                              lat: result.lat,
                              lng: result.lng,
                            };
                            dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: { newTripHotels: next } });
                          }}
                          placeholder="Search for a hotel..."
                          className="w-full"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col gap-1">
                          <label className="text-[8px] font-black uppercase text-zinc-400 tracking-widest px-0.5">Check-in</label>
                          <div className="flex flex-col gap-1">
                            <input
                              type="date"
                              className="w-full bg-white border border-zinc-200 px-2 py-1 text-[10px] font-bold outline-none focus:border-zinc-900"
                              value={hotel.startDate}
                              onChange={(e) => {
                                const next = [...state.newTripHotels];
                                next[idx].startDate = e.target.value;
                                dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: { newTripHotels: next } });
                              }}
                            />
                            <SmartTimeInput
                              className="w-full bg-white border border-zinc-200 px-2 py-1 text-[10px] font-bold outline-none focus:border-zinc-900"
                              value={hotel.checkInTime}
                              onChange={(val) => {
                                const next = [...state.newTripHotels];
                                next[idx].checkInTime = val;
                                dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: { newTripHotels: next } });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[8px] font-black uppercase text-zinc-400 tracking-widest px-0.5">Check-out</label>
                          <div className="flex flex-col gap-1">
                            <input
                              type="date"
                              className="w-full bg-white border border-zinc-200 px-2 py-1 text-[10px] font-bold outline-none focus:border-zinc-900"
                              value={hotel.endDate}
                              onChange={(e) => {
                                const next = [...state.newTripHotels];
                                next[idx].endDate = e.target.value;
                                dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: { newTripHotels: next } });
                              }}
                            />
                            <SmartTimeInput
                              className="w-full bg-white border border-zinc-200 px-2 py-1 text-[10px] font-bold outline-none focus:border-zinc-900"
                              value={hotel.checkOutTime}
                              onChange={(val) => {
                                const next = [...state.newTripHotels];
                                next[idx].checkOutTime = val;
                                dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: { newTripHotels: next } });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[8px] font-black uppercase text-zinc-400 tracking-widest px-0.5">Notes</label>
                        <input
                          type="text"
                          placeholder=""
                          className="w-full bg-white border border-zinc-200 px-2 py-1 text-[10px] font-bold outline-none focus:border-zinc-900"
                          value={hotel.notes}
                          onChange={(e) => {
                            const next = [...state.newTripHotels];
                            next[idx].notes = e.target.value;
                            dispatch({ type: 'SET_NEW_EVENT_INPUT', payload: { newTripHotels: next } });
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {state.newEventType === 'trip' && (
              <div className="flex flex-col gap-3 animate-in slide-in-from-top-2 duration-200 mt-2">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                    Location (City, State, Country)
                  </label>
                  <CitySearch
                    index={100}
                    city={{ name: state.newTripCity }}
                    onCityChange={(_, city) =>
                      dispatch({
                        type: 'SET_NEW_EVENT_INPUT',
                        payload: {
                          newTripCity: city.name,
                          newTripTimezone: city.timezone,
                          newTripLat: city.lat,
                          newTripLng: city.lng,
                        },
                      })
                    }                    placeholder="Search for a city..."
                    className="w-full"
                  />
                </div>
              </div>
            )}

            {state.newEventType !== 'trip' && !isProjectTask && (
              <div className="flex flex-col gap-4 p-1">


                <div className="flex items-center gap-6">
                  {state.newEventType !== 'birthday' && state.newEventType !== 'holiday' && state.newEventType !== 'task' && state.newEventType !== 'habit' && (
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div
                        onClick={() => {
                          if (state.newEventType === 'google-event') return;
                          dispatch({
                            type: 'SET_NEW_EVENT_INPUT',
                            payload: { newEventIsAllDay: !state.newEventIsAllDay },
                          })
                        }}
                        className={`w-10 h-6 rounded-none transition-all relative border-2 ${state.newEventIsAllDay ? 'bg-zinc-900 border-zinc-900' : 'bg-zinc-100 border-zinc-200'} ${state.newEventType === 'google-event' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div
                          className={`absolute top-1 w-3 h-3 rounded-none transition-all ${state.newEventIsAllDay ? 'left-5 bg-white' : 'left-1 bg-zinc-400'}`}
                        />
                      </div>
                      <span className="text-[10px] font-black uppercase text-zinc-600 tracking-widest">
                        All Day Event
                      </span>
                    </label>
                  )}

                </div>

                {state.newEventType === 'task' && !isProjectTask && (
                  <div className="flex flex-col gap-1 animate-in fade-in slide-in-from-top-1 duration-200 border-t border-zinc-100 pt-3">
                    <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                      Priority
                    </label>
                    <div className="flex items-center gap-2">
                      {['High', 'Medium', 'Low'].map((p) => (
                        <button
                          key={p}
                          onClick={() =>
                            dispatch({
                              type: 'SET_NEW_EVENT_INPUT',
                              payload: { newEventPriority: p },
                            })
                          }
                          className={`flex-1 py-1.5 text-[10px] font-black uppercase tracking-widest border-2 transition-colors ${state.newEventPriority === p ? 'border-green-600 bg-green-600 text-white' : 'border-zinc-200 bg-zinc-50 text-zinc-400 hover:bg-zinc-100'}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            )}

            {state.newEventType !== 'habit' && (state.newEventType === 'event' || state.newEventType === 'tentative' || state.newEventType === 'google-event') && (
              <div className="flex flex-col gap-1 animate-in fade-in slide-in-from-top-1 duration-200 border-t border-zinc-100 pt-3">
                <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                  PLACE
                </label>
                {state.newEventType === 'google-event' ? (
                  <div className="w-full bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-sm font-bold text-zinc-600 opacity-70">
                    {state.newEventAddress || 'No Location'}
                  </div>
                ) : (
                  <CitySearch
                    onCityChange={(_, city) =>
                      dispatch({
                        type: 'SET_NEW_EVENT_INPUT',
                        payload: { 
                          newEventVenueName: city.venueName || '',
                          newEventAddress: city.name || '',
                          newEventAddressLat: city.lat,
                          newEventAddressLng: city.lng,
                          newEventPlaceCity: city.name || '',
                          newEventPlaceTimezone: city.timezone || ''
                        },
                      })
                    }
                    city={{ name: state.newEventVenueName || state.newEventAddress }}
                    placeholder="Search for a business or address..."
                    className="w-full"
                    types={['geocode']}
                    locationBias={(() => {
                      if (state.newEventHasLocation && state.newEventLat) {
                        return { lat: state.newEventLat, lng: state.newEventLng };
                      }
                      const src = state.newEventTimezoneSource;
                      if (src === 'home') {
                        return authProfile?.cities?.[0] ? { lat: authProfile.cities[0].lat, lng: authProfile.cities[0].lng } : null;
                      }
                      if (src?.startsWith('trip:')) {
                        const tripId = src.split(':')[1];
                        const trip = (events || []).find((e) => e.id === tripId);
                        if (trip && trip.lat) return { lat: trip.lat, lng: trip.lng };
                      }
                      return null;
                    })()}
                  />
                )}
                {state.newEventVenueName && state.newEventAddress && (
                  <span className="text-[9px] text-zinc-400 font-medium px-1">
                    {state.newEventAddress}
                  </span>
                )}
              </div>
            )}

            {(() => {
              const dateStr = state.newEventStartDate;
              if (!dateStr || state.newEventType === 'trip' || state.newEventType === 'birthday' || state.newEventType === 'holiday' || state.newEventType === 'task') return null;

              if (state.newEventType === 'google-event') {
                return (
                  <div className="flex flex-col gap-2 p-1 bg-emerald-50/50 rounded-none p-4 border border-emerald-100">
                    <label className="text-[10px] font-black uppercase text-emerald-600 tracking-widest px-1">
                      Time Zone
                    </label>
                    <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 px-1 opacity-70">
                      {state.newEventTimezone || 'Default'}
                    </div>
                  </div>
                );
              }

              const allTrips = (events || []).filter((e) => e.type === 'trip');
              const outTrips = allTrips.filter((t) => t.endDate === dateStr);
              const stayTrips = allTrips.filter(
                (t) => t.startDate < dateStr && t.endDate > dateStr
              );
              const inTrips = allTrips.filter((t) => t.startDate === dateStr);

              const homeCity = authProfile?.cities?.[0] || { name: 'Home', timezone: 'home' };

              const options = [];
              const seen = new Set();

              const addOption = (city, source) => {
                const cityName = (city || 'Trip').split(',')[0].trim();
                const name = cityName.toUpperCase();
                if (seen.has(name)) return;
                seen.add(name);
                options.push({ name, source });
              };

              const isTravelDay = outTrips.length > 0 || inTrips.length > 0;

              if (isTravelDay) {
                // ORDER: Travel Out, Stay, Home, Travel In
                outTrips.forEach((t) => addOption(t.city, `trip:${t.id}`));
                stayTrips.forEach((t) => addOption(t.city, `trip:${t.id}`));
                addOption(homeCity.name, 'home');
                inTrips.forEach((t) => addOption(t.city, `trip:${t.id}`));
              } else {
                // Not a travel day: either in a trip (Stay) or at Home
                if (stayTrips.length > 0) {
                  stayTrips.forEach((t) => addOption(t.city, `trip:${t.id}`));
                } else {
                  addOption(homeCity.name, 'home');
                }
              }

              if (state.newEventPlaceTimezone) {
                addOption(state.newEventPlaceCity || 'Place', 'place');
              }
              
              if (state.newEventOverrideTimezone) {
                addOption(state.newEventOverrideCity || 'Override', 'override');
              }

              if (options.length === 0) return null;

              return (
                <div className="flex flex-col gap-2 p-1 bg-emerald-50/50 rounded-none p-4 border border-emerald-100">
                  <label className="text-[10px] font-black uppercase text-emerald-600 tracking-widest px-1">
                    Travel City (Time Zone)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {options.map((opt, idx) => {
                      const isActive = state.newEventTimezoneSource === opt.source;
                      return (
                        <button
                          key={`tz-opt-${idx}`}
                          onClick={() =>
                            dispatch({
                              type: 'SET_NEW_EVENT_INPUT',
                              payload: { newEventTimezoneSource: opt.source },
                            })
                          }
                          className={`flex-1 min-w-[80px] py-2 px-3 rounded-none text-[10px] font-black uppercase tracking-widest transition-all border ${isActive ? 'bg-emerald-500 border-emerald-500 text-white shadow-sm' : 'bg-transparent border-transparent text-zinc-400 hover:text-zinc-600'}`}
                        >
                          {opt.name}
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="mt-2 flex flex-col gap-1 border-t border-emerald-200/50 pt-3">
                     <label className="text-[10px] font-black uppercase text-emerald-600/70 tracking-widest px-1">
                        Custom City Search (Override Time Zone)
                     </label>
                     <CitySearch
                        onCityChange={(_, city) =>
                          dispatch({
                            type: 'SET_NEW_EVENT_INPUT',
                            payload: {
                              newEventOverrideCity: city.name,
                              newEventOverrideTimezone: city.timezone,
                              newEventTimezoneSource: 'override',
                            },
                          })
                        }
                        city={{ name: state.newEventTimezoneSource === 'override' ? state.newEventOverrideCity : '' }}
                        placeholder="Search any city to override timezone..."
                        className="w-full"
                     />
                  </div>
                </div>
              );
            })()}

            {!isProjectTask && (
              <div className="flex flex-col gap-1">
                <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                  Note
                </label>
                <textarea
                  disabled={state.newEventType === 'google-event'}
                  className={`w-full bg-zinc-50 border border-zinc-200 rounded-none px-2 py-1.5 text-sm font-bold outline-none focus:border-zinc-900 min-h-[50px] resize-none ${state.newEventType === 'google-event' ? 'opacity-70' : ''}`}
                  placeholder="Add notes or details..."
                  value={state.newEventNote}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_NEW_EVENT_INPUT',
                      payload: { newEventNote: e.target.value },
                    })
                  }
                />
              </div>
            )}

            <div className="flex flex-col gap-2 pt-2">
              {!isProjectTask && (
                <div className="flex flex-wrap items-center gap-2 pb-2">
                  {['event', 'tentative', 'task', 'birthday', 'holiday', 'habit', 'trip'].map((type) => (
                    <button
                      key={type}
                      disabled={
                        (state.newEventType === 'birthday' && type !== 'birthday') ||
                        (state.newEventType === 'holiday' && type !== 'holiday') ||
                        (state.newEventType === 'habit' && type !== 'habit') ||
                        isProjectTask
                      }
                      onClick={() => {
                        const isExisting = !!state.editingEventId;
                        const isConvertingToSpecial = ['birthday', 'holiday', 'habit', 'trip'].includes(type);

                        if (isExisting && isConvertingToSpecial && state.newEventType !== type) {
                          if (!window.confirm(`Converting this to a ${type.toUpperCase()} will change its format and may be permanent. Continue?`)) {
                            return;
                          }
                        }

                        if (isExisting && state.newEventType === 'habit' && type !== 'habit') {
                          alert('Habits cannot be converted back to regular events or tasks.');
                          return;
                        }
                        if (isExisting && state.newEventType === 'birthday' && type !== 'birthday') {
                          alert('Birthdays cannot be converted back to regular events or tasks.');
                          return;
                        }
                        if (isExisting && state.newEventType === 'holiday' && type !== 'holiday') {
                          alert('Holidays cannot be converted back to regular events or tasks.');
                          return;
                        }

                        let extraPayload = {};
                        if (!isExisting && type === 'habit') {
                          const now = new Date();
                          const todayId = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                          extraPayload = {
                            newEventEndDate: '',
                            newEventStartDate: state.newEventStartDate < todayId ? todayId : state.newEventStartDate
                          };
                        }

                        dispatch({
                          type: 'SET_NEW_EVENT_INPUT',
                          payload: { newEventType: type, ...extraPayload },
                        });
                      }}
                      className={`flex-1 py-1.5 text-[10px] font-black uppercase tracking-widest border-2 transition-colors ${state.newEventType === type ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 bg-zinc-50 text-zinc-400 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed'}`}
                    >
                      {type === 'tentative' ? 'tentative' : type}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => dispatch({ type: 'CLOSE_ADD_EVENT' })}
                  className="flex-1 py-3 bg-zinc-100 text-zinc-500 font-black uppercase tracking-widest text-xs rounded-none active:bg-zinc-200 transition-colors"
                >
                  {state.newEventType === 'google-event' ? 'Close' : 'Cancel'}
                </button>
                {state.newEventType !== 'google-event' && (
                  <button
                    onClick={layoutProps.handleSaveEvent}
                    className="flex-1 py-3 bg-zinc-900 text-white font-black uppercase tracking-widest text-xs rounded-none active:bg-black transition-colors shadow-lg"
                  >
                    Save
                  </button>
                )}
              </div>
              {state.editingEventId && state.newEventType !== 'google-event' && (
                <button
                  onClick={() => layoutProps.handleDeleteEvent(state.editingEventId)}
                  className="w-full py-3 bg-red-500/10 text-red-600 font-black uppercase tracking-widest text-xs rounded-none active:bg-red-500/20 transition-colors"
                >
                  Delete {state.newEventType === 'task' ? 'Task' : state.newEventType === 'habit' ? 'Habit' : 'Event'}
                </button>
              )}
            </div>
          </>
          );
          })()}
        </DraggableModal>
        )}

      {state.isDayDetailOpen && state.selectedDayId && (
        <DraggableModal
          dataTestId="day-detail-modal"
          onClose={() => dispatch({ type: 'CLOSE_DAY_DETAIL' })}
        >
          <div className="w-full max-w-[340px] h-[550px] flex flex-col overflow-hidden relative">
            <div className="bg-zinc-50 px-2 py-1.5 border-b border-zinc-100 flex flex-col relative">
              <button
                onClick={() => dispatch({ type: 'CLOSE_DAY_DETAIL' })}
                className="absolute top-3 right-3 p-1 hover:bg-zinc-200 rounded-full transition-colors text-zinc-400 hover:text-zinc-900"
              >
                <X size={16} />
              </button>
              <h2 className="text-base font-black text-zinc-900 tracking-tighter leading-none">
                {new Date(state.selectedDayId + 'T12:00:00').toLocaleDateString(undefined, {
                  weekday: 'long',
                })}
              </h2>
              <p className="text-[9px] font-black uppercase text-zinc-400 tracking-widest mt-0.5">
                {new Date(state.selectedDayId + 'T12:00:00').toLocaleDateString(undefined, {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-1 custom-scrollbar">
              {(() => {
                const dayItems = (layoutProps.calendarItems || [])
                  .filter(
                    (item) =>
                      state.selectedDayId >= item.startDate && state.selectedDayId <= item.endDate
                  )
                  .sort((a, b) => {
                    const getPriority = (item) => {
                      if (item.type === 'task') return 3;
                      if (item.isAllDay !== false) return 1;
                      return 2;
                    };
                    const pA = getPriority(a);
                    const pB = getPriority(b);
                    if (pA !== pB) return pA - pB;
                    return (a.startTime || '00:00').localeCompare(b.startTime || '00:00');
                  });

                if (dayItems.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center h-full opacity-10 py-10">
                      <CalendarIcon size={24} />
                      <p className="text-[8px] font-black uppercase tracking-widest mt-1">Empty</p>
                    </div>
                  );
                }

                return dayItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => {
                      if (item.type === 'project')
                        navigate(`/p/${item.id}` + window.location.search);
                      else if (item.type === 'task') {
                        const channelId = item.channelId;
                        if (channelId?.startsWith('personal-'))
                          navigate('/home' + window.location.search);
                        else navigate(`/p/${channelId}/tasks` + window.location.search);
                      } else if (!item.isReadOnly) {
                        dispatch({ type: 'OPEN_EDIT_EVENT', payload: item });
                      }
                      dispatch({ type: 'CLOSE_DAY_DETAIL' });
                    }}
                    className="px-2 py-1.5 rounded-lg flex flex-col gap-0 cursor-pointer transition-all hover:brightness-110 active:scale-[0.99] shadow-sm border border-black/5"
                    style={{ backgroundColor: layoutProps.getItemColor(item) }}
                  >
                    <div className="flex items-center gap-1.5">
                      {item.type === 'task' && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            layoutProps.toggleTask(item.id, item.completed);
                          }}
                          className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${item.completed ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/30'}`}
                        >
                          {item.completed && (
                            <Check size={7} strokeWidth={4} className="text-white" />
                          )}
                        </div>
                      )}
                      <span
                        className={`text-[10px] font-black uppercase tracking-tight text-white truncate ${item.completed ? 'line-through opacity-70' : ''}`}
                      >
                        {item.title}
                      </span>
                      {!item.isAllDay && item.displayTime && (
                        <div className="ml-auto flex items-center gap-1 text-[8px] font-black text-white/70 uppercase">
                          <span>{item.displayTime}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ));
              })()}
            </div>

            <button
              onClick={() => {
                dispatch({ type: 'OPEN_ADD_EVENT', payload: state.selectedDayId });
                dispatch({ type: 'CLOSE_DAY_DETAIL' });
              }}
              className="m-2 p-2 bg-zinc-900 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-black transition-colors shadow-lg active:scale-95"
            >
              <Plus size={14} />
              <span className="text-[9px] font-black uppercase tracking-widest">Add Event</span>
            </button>
          </div>
        </DraggableModal>
      )}

      {state.editingChannelId && (
        <DraggableModal
          dataTestId="edit-project-modal"
          onClose={() => dispatch({ type: 'CLOSE_EDIT_CHANNEL' })}
        >
          <div className="flex flex-col gap-1">
              <h2 className="text-xl font-black text-zinc-900 tracking-tighter">Edit Project</h2>
              <p className="text-xs text-zinc-400 font-medium">Update details or delete</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                Name
              </label>
              <input
                autoFocus
                type="text"
                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-2 py-1.5 text-lg font-bold outline-none focus:border-zinc-900"
                value={state.editChannelName}
                onChange={(e) =>
                  dispatch({
                    type: 'SET_EDIT_CHANNEL_INPUT',
                    payload: { editChannelName: e.target.value },
                  })
                }
                onKeyDown={(e) => e.key === 'Enter' && layoutProps.handleConfirmEditChannel()}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                Description
              </label>
              <input
                type="text"
                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-2 py-1.5 text-sm font-medium outline-none focus:border-zinc-900"
                placeholder="Add a description..."
                value={state.editChannelDescription}
                onChange={(e) =>
                  dispatch({
                    type: 'SET_EDIT_CHANNEL_INPUT',
                    payload: { editChannelDescription: e.target.value },
                  })
                }
              />
            </div>
            <div className="grid grid-cols-7 gap-2">
              {MUTED_COLORS.map((c) => (
                <button
                  key={c.id}
                  onClick={() =>
                    dispatch({
                      type: 'SET_EDIT_CHANNEL_INPUT',
                      payload: { editChannelColor: c.id },
                    })
                  }
                  className={`w-8 h-8 rounded-full border-2 ${state.editChannelColor === c.id ? 'border-zinc-900 scale-110 shadow-sm' : 'border-transparent'}`}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>

            {(() => {
              const cleanId = String(state.editingChannelId).replace(/^starred:/, '');
              const channel = (channels || []).find((c) => c.id === cleanId);
              if (!channel || channel.parentId) return null;

              return (
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest px-1">
                    Participants
                  </label>
                  <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-1 custom-scrollbar">
                    {(allUsers || []).filter(u => {
                      if (!u) return false;
                      const isP = (state.editChannelParticipants || []).includes(u.id);
                      const isMe = u.id === (effectiveUser?.uid || 'test-uid-123');
                      const friendEmails = (authProfile?.friends || []).map(e => e.toLowerCase());
                      const isFriend = u.email && friendEmails.includes(u.email.toLowerCase());
                      return isMe || isFriend || isP;
                    }).map((u) => {
                      const isP = (state.editChannelParticipants || []).includes(u.id);
                      const isMe = u.id === (effectiveUser?.uid || 'test-uid-123');
                      const isCreator = channel.createdBy === (effectiveUser?.uid || 'test-uid-123');
                      const isDisabled = isMe || !isCreator;
                      return (
                        <button
                          key={u.id}
                          disabled={isDisabled}
                          onClick={(e) => {
                            e.preventDefault();
                            if (isDisabled) return;
                            const nP = isP
                              ? state.editChannelParticipants.filter((id) => id !== u.id)
                              : [...state.editChannelParticipants, u.id];
                            dispatch({
                              type: 'SET_EDIT_CHANNEL_INPUT',
                              payload: { editChannelParticipants: nP },
                            });
                          }}
                          className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${isP ? 'bg-zinc-900 text-white border-zinc-900 shadow-md' : 'bg-zinc-50 text-zinc-500 border-zinc-200'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}
                        >
                          <Avatar src={u.avatar} size={18} />
                          <span className="truncate max-w-[100px]">
                            {u.name} {isMe && '(You)'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="flex flex-col gap-2 pt-2">
              <div className="flex gap-2">
                <button
                  onClick={() => dispatch({ type: 'CLOSE_EDIT_CHANNEL' })}
                  className="flex-1 py-3 bg-zinc-100 text-zinc-500 font-black uppercase tracking-widest text-[10px] rounded-xl active:bg-zinc-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={layoutProps.handleConfirmEditChannel}
                  className="flex-1 py-3 bg-zinc-900 text-white font-black uppercase tracking-widest text-[10px] rounded-xl active:bg-black transition-colors shadow-lg"
                >
                  Save
                </button>
              </div>
              <button
                onClick={() => layoutProps.handleDeleteChannel(state.editingChannelId)}
                className="w-full py-3 bg-red-500/10 text-red-600 font-black uppercase tracking-widest text-[10px] rounded-xl active:bg-red-500/20 transition-colors"
              >
                Delete Project
              </button>
            </div>
          </DraggableModal>
        )}

      {state.isLocationConflictOpen && state.pendingDragEvent && (
        <DraggableModal
          dataTestId="location-conflict-modal"
          onClose={() => dispatch({ type: 'CLOSE_LOCATION_CONFLICT' })}
        >
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-black text-zinc-900 tracking-tighter">Location Conflict</h2>
            <p className="text-xs text-zinc-400 font-medium leading-relaxed">
              Where will you be on {new Date(state.pendingDragEvent.startDate + 'T12:00:00').toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}?
            </p>
          </div>

          <div className="flex flex-col gap-2 p-1 bg-blue-50/50 rounded-2xl p-4 border border-blue-100">
            <label className="text-[10px] font-black uppercase text-blue-400 tracking-widest px-1">
              Select New Location
            </label>
            <div className="flex flex-wrap gap-2">
              {(() => {
                const dateStr = state.pendingDragEvent.startDate;
                const allTrips = (events || []).filter((e) => e.type === 'trip');
                const outTrips = allTrips.filter((t) => t.endDate === dateStr);
                const stayTrips = allTrips.filter(
                  (t) => t.startDate < dateStr && t.endDate > dateStr
                );
                const inTrips = allTrips.filter((t) => t.startDate === dateStr);
                const homeCity = authProfile?.cities?.[0] || { name: 'Home', timezone: 'home' };

                const options = [];
                const seen = new Set();
                const addOption = (city, source) => {
                  const name = (city || 'Trip').toUpperCase();
                  if (seen.has(name)) return;
                  seen.add(name);
                  options.push({ name, source });
                };

                const isTravelDay = outTrips.length > 0 || inTrips.length > 0;
                if (isTravelDay) {
                  outTrips.forEach((t) => addOption(t.city, `trip:${t.id}`));
                  stayTrips.forEach((t) => addOption(t.city, `trip:${t.id}`));
                  addOption(homeCity.name, 'home');
                  inTrips.forEach((t) => addOption(t.city, `trip:${t.id}`));
                } else {
                  if (stayTrips.length > 0)
                    stayTrips.forEach((t) => addOption(t.city, `trip:${t.id}`));
                  else addOption(homeCity.name, 'home');
                }

                return options.map((opt, idx) => (
                  <button
                    key={`conflict-tz-${idx}`}
                    onClick={async () => {
                      await safeUpdateDoc(
                        doc(
                          db,
                          'artifacts',
                          APP_ID_CONSTANT,
                          'public',
                          'data',
                          'events',
                          state.pendingDragEvent.id
                        ),
                        {
                          startDate: state.pendingDragEvent.startDate,
                          start: state.pendingDragEvent.startDate,
                          endDate: state.pendingDragEvent.endDate,
                          timezoneSource: opt.source,
                          updatedAt: serverTimestamp(),
                        }
                      );
                      dispatch({ type: 'CLOSE_LOCATION_CONFLICT' });
                    }}
                    className="flex-1 min-w-[80px] py-3 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border bg-white border-blue-200 text-blue-600 shadow-sm hover:brightness-95 active:scale-95"
                  >
                    {opt.name}
                  </button>
                ));
              })()}
            </div>
          </div>
          <button
            onClick={() => dispatch({ type: 'CLOSE_LOCATION_CONFLICT' })}
            className="w-full py-3 bg-zinc-100 text-zinc-500 font-black uppercase tracking-widest text-[10px] rounded-xl active:bg-zinc-200 transition-colors mt-2"
          >
            Cancel Move
          </button>
        </DraggableModal>
      )}

      <DragOverlay dropAnimation={null} zIndex={1000}>
        {state.activeId ? (
          <div className="flex items-center gap-3 p-4 bg-white border-2 border-zinc-900 shadow-2xl rounded-2xl opacity-90 cursor-grabbing min-w-[200px] max-w-[90vw]">
            {state.activeType === 'channel' ? (
              <>
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{
                    backgroundColor:
                      MUTED_COLORS.find(
                        (c) =>
                          c.id ===
                          channels.find(
                            (ch) => ch.id === String(state.activeId).replace(/^starred-/, '')
                          )?.color
                      )?.hex || '#e4e4e7',
                  }}
                />
                <span className="text-lg font-bold text-zinc-900 truncate">
                  {' '}
                  {channels.find((ch) => ch.id === String(state.activeId).replace(/^starred-/, ''))
                    ?.name || 'Project'}{' '}
                </span>
              </>
            ) : (
              <span className="text-sm font-bold text-zinc-900 truncate">
                {' '}
                {state.activeTitle || 'Item'}{' '}
              </span>
            )}
          </div>
        ) : null}
      </DragOverlay>
      {state.selectedProfileUser && (
        <UserProfileModal
          user={state.selectedProfileUser}
          onClose={() => dispatch({ type: 'SET_SELECTED_PROFILE', payload: null })}
          onAddBirthday={(user) => {
            dispatch({ type: 'SET_SELECTED_PROFILE', payload: null });
            
            const today = new Date();
            const y = today.getFullYear();
            let dateStr = `${y}-01-01`;
            if (user.birthday) {
              dateStr = `${y}-${user.birthday}`;
            }

            dispatch({
              type: 'SET_NEW_EVENT_INPUT',
              payload: {
                newEventId: 'new',
                newEventType: 'birthday',
                newEventTitle: `${user.name}'s Birthday`,
                newEventStartDate: dateStr,
                newEventEndDate: dateStr,
                newEventPersonName: user.name,
                newEventBirthYear: user.birthYear || '',
                newEventNotes: user.bio || '',
              }
            });
          }}
        />
      )}
      {lateNightHabitWarning && (
        <LateNightHabitModal
          habitName={
            habits?.find((h) => h.id === lateNightHabitWarning.habitId)?.title || 'Habit'
          }
          currentDayId={lateNightHabitWarning.dayId}
          onConfirm={(selectedDate) => {
            const { habitId, logData } = lateNightHabitWarning;
            const newMonthKey = selectedDate.substring(0, 7);
            setLateNightHabitWarning(null);
            layoutProps.handleLogHabit(newMonthKey, selectedDate, habitId, logData, true);
          }}
          onCancel={() => setLateNightHabitWarning(null)}
        />
      )}
    </DndContext>
    </>
  );
}

export default function App() {
  const queryParams = new URLSearchParams(window.location.search);
  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isVaultAccess = isLocalHost && (queryParams.get('access') === 'kx92j' || window.__IS_VAULT_ACCESS);
  const isIdle = useIdleGate(!isVaultAccess);

  return (
    <AuthProvider>
      {isIdle ? (
        <IdleRefreshOverlay />
      ) : (
        <ProjectDataProvider isVault={isVaultAccess}>
          <AppContent />
        </ProjectDataProvider>
      )}
    </AuthProvider>
  );
}
